import React, { useState } from 'react';
import { csvCell } from '../../lib/csvCell';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { useModalLayer } from '../../lib/modalStack';
import { db, auth, handleFirestoreError, OperationType } from '../../lib/firebase';
import { collection, getDocs, setDoc, updateDoc, doc, deleteDoc, query } from 'firebase/firestore';
import MasterPipeline, { MASTER_STAGES } from '../MasterPipeline';
import { getPipelineStages, stageLabel } from '../../lib/programPipeline';
import { Application, ApplicationStatus, Event } from '../../types';
import { COMMON_TIMEZONES } from '../../lib/static-data';
import {
  X, Plus, Trash2, MapPin, Clock, Calendar, RefreshCw, Search, Filter,
  ChevronLeft, ChevronRight, Eye, Link, List, Shield, MessageSquare,
  Award, Briefcase, Compass, AlertCircle, Edit3, FileText, User, Users, Download, Video,
  Loader, Send, CheckCircle, Check, ExternalLink
} from 'lucide-react';
import ImproveTextDropdown from '../shared/ImproveTextDropdown';
import Modal from '../shared/Modal';
import SearchableSelect from '../shared/SearchableSelect';
import SchedulingCalendar from '../shared/SchedulingCalendar';
import { usePrograms } from '../../hooks/usePrograms';
import { RichTextEditor, RichTextContent } from '../shared/RichTextEditor';
import { usePath, navigate, parseRoute } from '../../lib/router';
import { getSafeApplicantName, sanitizeNamePart } from '../../lib/nameUtils';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';

interface EventsManagerProps {
  canEdit?: boolean;
  events: Event[];
  applications: Application[];
  isCreatingEvent: boolean;
  isEditingEventId: string | null;
  eventViewMode: 'calendar' | 'list';
  calendarViewType: 'month' | 'week' | 'day';
  currentCalendarDate: Date;
  eventSearchQuery: string;
  filterEventType: string;
  filterEventStatus: string;
  filterEventApplicant: string;
  filterDateStart: string;
  filterDateEnd: string;
  filterDatePreset: string;
  focusedEvent: Event | null;
  filteredEvents: Event[];
  eventsPage: number;
  eventsPageSize: number;
  eventsTotalPages: number;
  displayedEvents: Event[];
  eventTitle: string;
  eventType: string;
  eventDateTime: string;
  eventTimezone: string;
  eventLocation: string;
  eventDescription: string;
  eventInternalNotes: string;
  eventExternalNotes: string;
  eventStatus: string;
  eventApplicantId: string;
  eventNotifyApplicants: boolean;
  isVirtualMeeting: boolean;
  eventAssignedTo: string;
  eventAssignedToName: string;
  agentsList: { uid: string; name: string; email: string; role?: string }[];
  agentsLoaded: boolean;
  agentsError: boolean;
  adminEmail: string;
  setEventsPage: (p: number) => void;
  setEventViewMode: (m: 'calendar' | 'list') => void;
  setCurrentCalendarDate: (d: Date) => void;
  setEventSearchQuery: (q: string) => void;
  setFilterEventType: (t: string) => void;
  setFilterEventStatus: (s: string) => void;
  setFilterEventApplicant: (a: string) => void;
  setFilterDateStart: (d: string) => void;
  setFilterDateEnd: (d: string) => void;
  setFilterDatePreset: (p: string) => void;
  setFocusedEvent: (e: Event | null) => void;
  setEventTitle: (t: string) => void;
  setEventType: (t: string) => void;
  setEventDateTime: (d: string) => void;
  setEventTimezone: (tz: string) => void;
  setEventLocation: (l: string) => void;
  setEventDescription: (d: string) => void;
  setEventInternalNotes: (n: string) => void;
  setEventExternalNotes: (n: string) => void;
  setEventStatus: (s: string) => void;
  setEventApplicantId: (a: string) => void;
  setEventNotifyApplicants: (v: boolean) => void;
  setIsVirtualMeeting: (v: boolean) => void;
  setEventAssignedTo: (a: string) => void;
  setEventAssignedToName: (n: string) => void;
  setIsCreatingEvent: (c: boolean) => void;
  setIsEditingEventId: (id: string | null) => void;
  onFetchEvents: () => void;
  onFetchAgents: () => void;
  onSaveEvent: () => void;
  onDeleteEvent: (id: string, title: string) => void;
  onEditEventClick: (ev: Event) => void;
  onViewApplicant: (app: Application) => void;
}

export default function EventsManager(props: EventsManagerProps) {
  const {
    canEdit = true,
    events, applications, isCreatingEvent, isEditingEventId,
    isSavingEvent,
    eventViewMode, calendarViewType, currentCalendarDate,
    eventSearchQuery, filterEventType, filterEventStatus, filterEventApplicant,
    filterDateStart, filterDateEnd, filterDatePreset, focusedEvent,
    filteredEvents, eventsPage, eventsPageSize, eventsTotalPages, displayedEvents,
    eventTitle, eventType, eventDateTime, eventTimezone, eventLocation, eventDescription,
    eventInternalNotes, eventExternalNotes, eventStatus,
    eventApplicantId, eventNotifyApplicants, adminEmail,
    isVirtualMeeting, eventAssignedTo, eventAssignedToName, agentsList, agentsLoaded, agentsError,
    setEventsPage, setEventViewMode, setCurrentCalendarDate,
    setEventSearchQuery, setFilterEventType, setFilterEventStatus,
    setFilterEventApplicant, setFilterDateStart, setFilterDateEnd, setFilterDatePreset,
    setFocusedEvent, setEventTitle, setEventType, setEventDateTime, setEventTimezone, setEventLocation,
    setEventDescription, setEventInternalNotes, setEventExternalNotes, setEventStatus, setEventApplicantId, setEventNotifyApplicants,
    setIsVirtualMeeting, setEventAssignedTo, setEventAssignedToName,
    setIsCreatingEvent, setIsEditingEventId,
    onFetchEvents: fetchEvents,
    onFetchAgents: fetchAgents,
    onSaveEvent: handleSaveEvent,
    onDeleteEvent: handleDeleteEvent,
    onEditEventClick: handleEditEventClick,
    onViewApplicant,
  } = props;

  // Organizer decision on a pending reschedule proposal (status 'Pending').
  const handleProposalDecision = async (ev: any, decision: 'approve' | 'reject') => {
    if (!(await confirmDialog(`${decision === 'approve' ? 'Approve' : 'Reject'} the requested new time for "${ev.title}"?`))) return;
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetch(`/api/scheduling/proposal/${ev.eventId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ decision }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      fetchEvents();
      notifyToast('success', decision === 'approve' ? 'Reschedule approved. The meeting is now confirmed.' : 'Reschedule rejected.');
    } catch (err: any) {
      notifyToast('error', 'Failed to update proposal: ' + err.message);
    }
  };

  // Fetch agents list on mount for scheduling invite
  React.useEffect(() => { fetchAgents(); }, [fetchAgents]);

  // ── Local state for date-click popup ──
  const [datePopupEvents, setDatePopupEvents] = useState<Event[]>([]);
  const [datePopupDate, setDatePopupDate] = useState<Date | null>(null);
  const [slideInEvent, setSlideInEvent] = useState<Event | null>(null);

  const currentPath = usePath();
  const currentRoute = parseRoute(currentPath);

  // Sync focusedEvent with URL path.
  //
  // #1125 Item 5 (sibling of the EmployersManager fix): the focused
  // state must not be a dependency of this route-sync effect — setting
  // it from a row action while on the list path would make this effect
  // clear it on the same commit. Compare through a ref instead.
  const focusedEventIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    focusedEventIdRef.current = focusedEvent?.eventId ?? null;
  }, [focusedEvent]);

  React.useEffect(() => {
    if (currentRoute.type === 'events_detail') {
      const targetId = currentRoute.id;
      if (focusedEventIdRef.current !== targetId) {
        const found = events.find(e => e.eventId === targetId);
        if (found) {
          setFocusedEvent(found);
        }
      }
    } else if (currentPath === '/events' || currentPath === '/myevents') {
      if (focusedEventIdRef.current) {
        setFocusedEvent(null);
        setSlideInEvent(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, events]);

  // Sync URL path when focusedEvent changes
  React.useEffect(() => {
    if (focusedEvent) {
      navigate(`/myevents/adminview/${focusedEvent.eventId}`);
    } else if (currentRoute.type === 'events_detail') {
      navigate('/myevents');
    }
  }, [focusedEvent]);

  const { programs } = usePrograms();
  const uniqueTerms = React.useMemo(() => {
    return Array.from(new Set(applications.map(a => a.programTerm).filter(Boolean))) as string[];
  }, [applications]);

  // ── GROUP EVENT SPECIFIC STATES ──
  const [isGroupEventForm, setIsGroupEventForm] = useState(false);
  const [isSavingGroupEvent, setIsSavingGroupEvent] = useState(false);
  const [eventDate, setEventDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('09:00');
  const [eventEndTime, setEventEndTime] = useState('17:00');
  const [sessionDuration, setSessionDuration] = useState(15);
  const [bufferBefore, setBufferBefore] = useState(0);
  const [bufferAfter, setBufferAfter] = useState(0);
  const [blockedTimes, setBlockedTimes] = useState<BlockedPeriod[]>([]);
  const [bookingOpensType, setBookingOpensType] = useState<'immediately' | 'scheduled'>('immediately');
  const [bookingOpensAt, setBookingOpensAt] = useState('');
  const [bookingClosesType, setBookingClosesType] = useState<'hoursBefore' | 'scheduled'>('hoursBefore');
  const [bookingClosesHours, setBookingClosesHours] = useState(24);
  const [bookingClosesAt, setBookingClosesAt] = useState('');
  const [publicNoInvite, setPublicNoInvite] = useState(false);
  const [approvalRequired, setApprovalRequired] = useState(false);

  // Assigned workspace team (admins/staff) for GROUP events. Mirrors the
  // regular-event single "assign to agent" concept but allows many staff to run
  // one group event. The first pick is mirrored into assignedTo/assignedToName
  // for calendar/tooling compatibility.
  const [groupAssignedUids, setGroupAssignedUids] = useState<string[]>([]);

  // Local video provider state
  const [videoProvider, setVideoProvider] = useState<'jitsi' | 'daily'>('jitsi');

  // Eligibility requirements states
  const [eligibleProgramId, setEligibleProgramId] = useState('all');
  const [eligibleTerm, setEligibleTerm] = useState('all');
  const [eligibleStage, setEligibleStage] = useState('all');
  const [eligibleStatus, setEligibleStatus] = useState('all');

  // Blocked time input states
  const [newBlockLabel, setNewBlockLabel] = useState('');
  const [newBlockStart, setNewBlockStart] = useState('12:00');
  const [newBlockEnd, setNewBlockEnd] = useState('13:00');

  // Dashboard state variables
  const [dashboardTab, setDashboardTab] = useState<'overview' | 'slots' | 'broadcast' | 'invitations' | 'notes' | 'results'>('overview');
  const [selectedAttendeeEmails, setSelectedAttendeeEmails] = useState<string[]>([]);
  const [selectedPendingSlotIds, setSelectedPendingSlotIds] = useState<string[]>([]);
  const [selectedInvitedAppIds, setSelectedInvitedAppIds] = useState<string[]>([]);
  const [approvingSlot, setApprovingSlot] = useState(false);

  const handleApproveSlot = async (slotIds: string[], action: 'approve' | 'decline') => {
    const targetEvent = focusedEvent || slideInEvent;
    if (!targetEvent) return;
    try {
      setApprovingSlot(true);
      const res = await safeFetch(`/api/admin/group-events/${targetEvent.eventId}/approve-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slotIds, action })
      });

      // Immediate UI update for seamless local state synchronization
      const updatedSlots = (targetEvent.slots || []).map((slot: any) => {
        if (!slotIds.includes(slot.id)) return slot;
        if (action === 'approve') {
          return {
            ...slot,
            bookingStatus: 'booked',
            checkInStatus: 'pending',
          };
        } else {
          const { bookedBy, checkInStatus, meetingUrl, ...restSlot } = slot;
          return {
            ...restSlot,
            bookingStatus: 'unbooked'
          };
        }
      });

      const updatedInvitations = { ...(targetEvent.invitations || {}) };
      slotIds.forEach(id => {
        const slot = (targetEvent.slots || []).find((s: any) => s.id === id);
        if (slot?.bookedBy?.applicationId && updatedInvitations[slot.bookedBy.applicationId]) {
          updatedInvitations[slot.bookedBy.applicationId] = {
            ...updatedInvitations[slot.bookedBy.applicationId],
            status: action === 'approve' ? 'Registered' : 'Declined'
          };
        }
      });

      const updatedEvent = {
        ...targetEvent,
        slots: updatedSlots,
        invitations: updatedInvitations
      };

      if (focusedEvent && focusedEvent.eventId === targetEvent.eventId) {
        setFocusedEvent(updatedEvent);
      }
      if (slideInEvent && slideInEvent.eventId === targetEvent.eventId) {
        setSlideInEvent(updatedEvent);
      }

      notifyToast('success', res.message || `Slot booking(s) ${action === 'approve' ? 'approved' : 'declined'}.`);
      setSelectedPendingSlotIds([]);
      await fetchEvents();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to update slot booking status.');
    } finally {
      setApprovingSlot(false);
    }
  };
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);

  // Invitations tab search and filter states
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteProgramFilter, setInviteProgramFilter] = useState('all');
  const [inviteTermFilter, setInviteTermFilter] = useState('all');
  const [inviteStageFilter, setInviteStageFilter] = useState('all');
  const [inviteStatusFilter, setInviteStatusFilter] = useState('all');
  const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);

  // Manual assignment state
  const [assigningSlotId, setAssigningSlotId] = useState<string | null>(null);
  const [assignName, setAssignName] = useState('');
  const [assignEmail, setAssignEmail] = useState('');
  const [assignAppId, setAssignAppId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Rescheduling state
  const [reschedulingSlotId, setReschedulingSlotId] = useState<string | null>(null);

  // Auto-scroll to top of scrollable dashboard panels when assigning or rescheduling is activated
  React.useEffect(() => {
    if (assigningSlotId || reschedulingSlotId) {
      // Find the scrollable modal container
      const panels = document.querySelectorAll('.overflow-y-auto');
      panels.forEach(p => {
        p.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }, [assigningSlotId, reschedulingSlotId]);

  // Group Event Notes and Results Dashboard states
  const [groupNotesInternalDraft, setGroupNotesInternalDraft] = useState('');
  const [groupNotesExternalDraft, setGroupNotesExternalDraft] = useState('');
  const [savingGroupNotes, setSavingGroupNotes] = useState(false);
  const [submittingResultSlotId, setSubmittingResultSlotId] = useState<string | null>(null);
  const [selectedSlotResults, setSelectedSlotResults] = useState<Record<string, string>>({});

  // Individual Event Tab & Draft states
  const [individualTab, setIndividualTab] = useState<'details' | 'notes' | 'results'>('details');
  const [individualNotesInternalDraft, setIndividualNotesInternalDraft] = useState('');
  const [individualNotesExternalDraft, setIndividualNotesExternalDraft] = useState('');
  const [savingIndividualNotes, setSavingIndividualNotes] = useState(false);
  const [submittingIndividualResult, setSubmittingIndividualResult] = useState(false);
  const [individualResultValue, setIndividualResultValue] = useState('');

  // Auto-sync notes drafts when event focus changes
  React.useEffect(() => {
    const ev = focusedEvent || slideInEvent;
    if (ev) {
      if (ev.isGroupEvent) {
        setGroupNotesInternalDraft(ev.internalNotes || '');
        setGroupNotesExternalDraft(ev.externalNotes || ev.description || '');
      } else {
        setIndividualNotesInternalDraft(ev.internalNotes || '');
        setIndividualNotesExternalDraft(ev.externalNotes || ev.description || '');
      }
    }
  }, [focusedEvent, slideInEvent]);

  // ── GROUP EVENT UTILITIES & HANDLERS ──
  const timeToMinutes = (t: string): number => {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const minutesToTime = (m: number): string => {
    const h = Math.floor(m / 60) % 24;
    const min = m % 60;
    return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
  };

  const generateSlotsPreview = () => {
    if (!eventStartTime || !eventEndTime || !sessionDuration) return [];
    const startMin = timeToMinutes(eventStartTime);
    const endMin = timeToMinutes(eventEndTime);
    const D = Number(sessionDuration);
    const B_before = Number(bufferBefore || 0);
    const B_after = Number(bufferAfter || 0);

    const preview: { id: string; startTime: string; endTime: string }[] = [];
    let currentStart = startMin;
    let index = 0;

    while (currentStart + B_before + D + B_after <= endMin) {
      const slotStart = currentStart + B_before;
      const slotEnd = slotStart + D;

      // Check overlap with any blocked times
      const hasOverlap = blockedTimes.some(block => {
        const bStart = timeToMinutes(block.startTime);
        const bEnd = timeToMinutes(block.endTime);
        return Math.max(slotStart, bStart) < Math.min(slotEnd, bEnd);
      });

      if (!hasOverlap) {
        preview.push({
          id: `slot_${index++}`,
          startTime: minutesToTime(slotStart),
          endTime: minutesToTime(slotEnd),
        });
      }

      currentStart = slotStart + D + B_after;
    }
    return preview;
  };

  const handleSaveGroupEvent = async () => {
    if (isSavingGroupEvent) return;
    setIsSavingGroupEvent(true);
    if (!eventTitle.trim() || !eventDate || !eventStartTime || !eventEndTime) {
      notifyToast('warning', 'Please fill out all required fields (title, date, start time, end time).');
      setIsSavingGroupEvent(false);
      return;
    }

    const preview = generateSlotsPreview();
    if (preview.length === 0) {
      notifyToast('warning', 'No slots were generated. Please check your session duration and timeline boundaries.');
      setIsSavingGroupEvent(false);
      return;
    }

    // Preserve existing bookings if editing
    const existingSlotsMap = new Map<string, any>();
    const currentEventInEffect = slideInEvent || focusedEvent;
    if (isEditingEventId && currentEventInEffect && currentEventInEffect.slots) {
      currentEventInEffect.slots.forEach((s: any) => {
        if (s.bookedBy) {
          existingSlotsMap.set(s.startTime, s);
        }
      });
    }

    if (isEditingEventId && existingSlotsMap.size > 0) {
      const origDate = currentEventInEffect?.dateTime ? new Date(currentEventInEffect.dateTime).toISOString().slice(0, 10) : '';
      if (origDate && origDate !== eventDate) {
        if (!(await confirmDialog(`Warning: This group event already has ${existingSlotsMap.size} candidate booking(s). Updating the event date from ${origDate} to ${eventDate} will alter the event schedule for booked applicants. Are you sure you want to proceed?`))) {
          setIsSavingGroupEvent(false);
          return;
        }
      }
    }

    const finalSlots = preview.map(p => {
      if (existingSlotsMap.has(p.startTime)) {
        return existingSlotsMap.get(p.startTime);
      }
      return {
        id: p.id,
        startTime: p.startTime,
        endTime: p.endTime,
        bookingStatus: 'unbooked',
        checkInStatus: 'pending'
      };
    });

    const combinedIso = new Date(`${eventDate}T${eventStartTime}:00`).toISOString();

    const payload = {
      eventId: isEditingEventId || undefined,
      title: eventTitle.trim(),
      eventType,
      dateTime: combinedIso,
      timezone: eventTimezone,
      location: eventLocation.trim(),
      description: eventDescription.trim() || eventExternalNotes.trim() || '',
      externalNotes: eventExternalNotes.trim() || eventDescription.trim() || '',
      internalNotes: eventInternalNotes.trim() || '',
      status: eventStatus,
      slots: finalSlots,
      groupEventConfig: {
        sessionDuration,
        bufferBefore,
        bufferAfter,
        blockedTimes,
        bookingOpensType,
        bookingOpensAt: bookingOpensType === 'scheduled' ? bookingOpensAt : undefined,
        bookingClosesType: bookingClosesType,
        bookingClosesAt: bookingClosesType === 'scheduled' ? bookingClosesAt : undefined,
        bookingClosesHours: bookingClosesType === 'hoursBefore' ? bookingClosesHours : undefined,
        isVirtual: isVirtualMeeting,
        meetingDestination: isVirtualMeeting ? videoProvider : (eventLocation.startsWith('http') ? 'url' : 'physical'),
        meetingDetails: eventLocation.trim(),
        location: isVirtualMeeting ? (videoProvider === 'daily' ? 'Virtual Daily.co Meeting' : 'Virtual Jitsi Meeting') : eventLocation.trim(),
        eligibleProgramId: eligibleProgramId === 'all' ? '' : eligibleProgramId,
        eligibleTerm: eligibleTerm === 'all' ? '' : eligibleTerm,
        eligibleStage: eligibleStage === 'all' ? '' : eligibleStage,
        eligibleStatus: eligibleStatus === 'all' ? '' : eligibleStatus,
        publicNoInvite,
        approvalRequired,
      }
    };

    // Workspace team assignment (mirror first pick into assignedTo/assignedToName
    // so calendars and legacy "assigned to" tooling keep working).
    const assignedTeam = agentsList
      .filter(agent => groupAssignedUids.includes(agent.uid))
      .map(agent => ({ uid: agent.uid, name: agent.name, email: agent.email || '', role: agent.role || '' }));
    if (assignedTeam.length > 0) {
      payload.assignedTeam = assignedTeam;
      payload.assignedTo = assignedTeam[0].uid;
      payload.assignedToName = assignedTeam[0].name;
      payload.assignedToRole = 'admin';
    } else {
      payload.assignedTeam = [];
      delete payload.assignedTo;
      delete payload.assignedToName;
      delete payload.assignedToRole;
    }

    try {
      await safeFetch('/api/admin/group-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      fetchEvents();
      setIsCreatingEvent(false);
      setIsEditingEventId(null);
      setFocusedEvent(null);
      setSlideInEvent(null);
      notifyToast('success', `Group Event "${eventTitle.trim()}" saved successfully.`);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save group event.');
    } finally {
      setIsSavingGroupEvent(false);
    }
  };

  const handleEditEventClickLocal = (ev: Event) => {
    setIsGroupEventForm(!!ev.isGroupEvent);
    if (ev.isGroupEvent) {
      setIsEditingEventId(ev.eventId);
      setIsCreatingEvent(false);
      setEventTitle(ev.title);
      setEventType(ev.eventType as any);
      let safeDate = '';
      if (ev.dateTime && typeof ev.dateTime === 'string') {
        try {
          safeDate = ev.dateTime.slice(0, 10);
        } catch { }
      }
      setEventDate(safeDate);
      const cfg = ev.groupEventConfig || {
        sessionDuration: 15,
        bufferBefore: 0,
        bufferAfter: 0,
        blockedTimes: [],
        bookingOpensType: 'immediately',
        bookingClosesType: 'hoursBefore',
        bookingClosesHours: 24,
        isVirtual: false
      };
      setPublicNoInvite(cfg.publicNoInvite === true || cfg.isPublic === true || cfg.requiresInvitation === false);
      setApprovalRequired(cfg.approvalRequired === true || cfg.requiresApproval === true);
      const slots = ev.slots || [];
      if (slots.length > 0) {
        setEventStartTime(slots[0].startTime);
        setEventEndTime(slots[slots.length - 1].endTime);
      } else {
        setEventStartTime('09:00');
        setEventEndTime('17:00');
      }
      setEventTimezone(ev.timezone || 'America/New_York');
      setEventLocation(ev.location || '');
      setEventDescription(ev.description || '');
      setEventInternalNotes(ev.internalNotes || '');
      setEventExternalNotes(ev.externalNotes || ev.description || '');
      setEventStatus(ev.status as any);
      setIsVirtualMeeting(cfg.isVirtual || false);
      setSessionDuration(cfg.sessionDuration || 15);
      setBufferBefore(cfg.bufferBefore || 0);
      setBufferAfter(cfg.bufferAfter || 0);
      setBlockedTimes(cfg.blockedTimes || []);
      setBookingOpensType(cfg.bookingOpensType || 'immediately');
      setBookingOpensAt(cfg.bookingOpensAt ? cfg.bookingOpensAt.slice(0, 16) : '');
      setBookingClosesType(cfg.bookingClosesType || 'hoursBefore');
      setBookingClosesHours(cfg.bookingClosesHours || 24);
      setBookingClosesAt(cfg.bookingClosesAt ? cfg.bookingClosesAt.slice(0, 16) : '');
      setEligibleProgramId(cfg.eligibleProgramId || 'all');
      setEligibleTerm(cfg.eligibleTerm || 'all');
      setEligibleStage(cfg.eligibleStage || 'all');
      setEligibleStatus(cfg.eligibleStatus || 'all');
      const teamUids = Array.isArray(ev.assignedTeam)
        ? ev.assignedTeam.map((m: any) => m.uid || m.userId || m.id).filter(Boolean)
        : (ev.assignedTo ? [ev.assignedTo] : []);
      setGroupAssignedUids(teamUids);
    } else {
      setIsCreatingEvent(false);
      setIsEditingEventId(ev.eventId);
      handleEditEventClick(ev);
    }
  };

  const handleAssignSlot = async (slotId: string) => {
    if (!assignName.trim() || !assignEmail.trim()) {
      notifyToast('warning', 'Please select an applicant or enter their details.');
      return;
    }
    setAssigning(true);
    try {
      await safeFetch(`/api/admin/group-events/${slideInEvent?.eventId || focusedEvent?.eventId}/assign-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slotId,
          name: assignName.trim(),
          email: assignEmail.trim(),
          applicationId: assignAppId || undefined
        }),
      });

      fetchEvents();
      // Reset assignment state
      setAssigningSlotId(null);
      setAssignName('');
      setAssignEmail('');
      setAssignAppId('');
      setSearchQuery('');
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to manually allocate slot.');
    } finally {
      setAssigning(false);
    }
  };

  const handleCancelSlotBooking = async (slotId: string) => {
    if (!(await confirmDialog('Are you sure you want to cancel this slot booking? This slot will immediately become bookable again.'))) return;
    try {
      await safeFetch(`/api/admin/group-events/${slideInEvent?.eventId || focusedEvent?.eventId}/cancel-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slotId }),
      });

      fetchEvents();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to cancel slot.');
    }
  };

  const handleRescheduleBooking = async (oldSlotId: string, newSlotId: string, bookedBy: any) => {
    try {
      const targetEventId = slideInEvent?.eventId || focusedEvent?.eventId;

      // 1. Assign to new slot
      await safeFetch(`/api/admin/group-events/${targetEventId}/assign-slot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slotId: newSlotId,
          name: bookedBy.name,
          email: bookedBy.email,
          applicationId: bookedBy.applicationId
        }),
      });

      // 2. Cancel old slot
      try {
        await safeFetch(`/api/admin/group-events/${targetEventId}/cancel-slot`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slotId: oldSlotId }),
        });
      } catch (cErr) {
        console.warn('New slot allocated but old slot cancel failed:', cErr);
      }

      fetchEvents();
      setReschedulingSlotId(null);
      notifyToast('success', 'Rescheduled successfully!');
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to reschedule booking.');
    }
  };

  const handleSendBroadcast = async () => {
    if (selectedAttendeeEmails.length === 0) {
      notifyToast('warning', 'Please select at least one recipient.');
      return;
    }
    if (!broadcastSubject.trim() || !broadcastMessage.trim()) {
      notifyToast('warning', 'Please provide a subject and a message.');
      return;
    }
    setBroadcasting(true);
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const eventId = slideInEvent?.eventId || focusedEvent?.eventId;
      const items = selectedAttendeeEmails.map((email: string) => ({
        itemType: 'broadcast_recipient',
        recipientEmail: email,
        inputSnapshot: { email, subject: broadcastSubject.trim(), htmlBody: `<p>${broadcastMessage.trim()}</p>` },
      }));

      const res = await fetch('/api/admin/bulk-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: 'event_broadcast_email',
          items,
          inputSnapshot: { eventId },
          options: { subject: broadcastSubject.trim(), htmlBody: `<p>${broadcastMessage.trim()}</p>` },
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to enqueue broadcast job.');
      notifyToast('success', 'Event Broadcast Task Queued! Tracking progress in the Task Tracker.');
      window.dispatchEvent(new CustomEvent('bulk-job-created'));
      setBroadcastSubject('');
      setBroadcastMessage('');
      setDashboardTab('slots');
      notifyToast('success', 'Event Broadcast Task Queued! Tracking progress in the Task Tracker.');
      window.dispatchEvent(new CustomEvent('bulk-job-created'));
    } catch (err: any) {
      notifyToast('error', err.message);
    } finally {
      setBroadcasting(false);
    }
  };

  const handleSaveGroupDashboardNotes = async (targetEventId: string) => {
    try {
      setSavingGroupNotes(true);
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetch(`/api/admin/events/${targetEventId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          internalNotes: groupNotesInternalDraft,
          externalNotes: groupNotesExternalDraft
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save notes');
      }
      notifyToast('success', 'Event notes updated successfully.');
      fetchEvents(); // reload list
      // Update in-memory focused/slide-in event notes
      if (focusedEvent && focusedEvent.eventId === targetEventId) {
        setFocusedEvent({ ...focusedEvent, internalNotes: groupNotesInternalDraft, externalNotes: groupNotesExternalDraft, description: groupNotesExternalDraft });
      }
      if (slideInEvent && slideInEvent.eventId === targetEventId) {
        setSlideInEvent({ ...slideInEvent, internalNotes: groupNotesInternalDraft, externalNotes: groupNotesExternalDraft, description: groupNotesExternalDraft });
      }
    } catch (err: any) {
      notifyToast('error', err.message);
    } finally {
      setSavingGroupNotes(false);
    }
  };

  const handleSaveGroupSlotResult = async (targetEventId: string, slotId: string, resultVal: string) => {
    try {
      setSubmittingResultSlotId(slotId);
      const data = await safeFetch(`/api/admin/group-events/${targetEventId}/slots/${slotId}/result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ result: resultVal })
      });
      notifyToast('success', `Interview status updated to "${resultVal}" and candidate has been notified.`);
      fetchEvents();
      // Update in-memory slots
      const updatedSlots = (focusedEvent?.slots || slideInEvent?.slots || []).map((s: any) => s.id === slotId ? { ...s, result: resultVal } : s);
      if (focusedEvent && (focusedEvent.eventId === targetEventId || focusedEvent.id === targetEventId)) {
        setFocusedEvent({ ...focusedEvent, slots: updatedSlots });
      }
      if (slideInEvent && (slideInEvent.eventId === targetEventId || slideInEvent.id === targetEventId)) {
        setSlideInEvent({ ...slideInEvent, slots: updatedSlots });
      }
    } catch (err: any) {
      notifyToast('error', err.message);
    } finally {
      setSubmittingResultSlotId(null);
    }
  };

  const handleSaveIndividualNotes = async (targetEventId: string) => {
    try {
      setSavingIndividualNotes(true);
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetch(`/api/admin/events/${targetEventId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          internalNotes: individualNotesInternalDraft,
          externalNotes: individualNotesExternalDraft
        })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save notes');
      }
      notifyToast('success', 'Event notes updated successfully.');
      fetchEvents();
      // Update in-memory focused/slide-in event notes
      if (focusedEvent && focusedEvent.eventId === targetEventId) {
        setFocusedEvent({ ...focusedEvent, internalNotes: individualNotesInternalDraft, externalNotes: individualNotesExternalDraft, description: individualNotesExternalDraft });
      }
      if (slideInEvent && slideInEvent.eventId === targetEventId) {
        setSlideInEvent({ ...slideInEvent, internalNotes: individualNotesInternalDraft, externalNotes: individualNotesExternalDraft, description: individualNotesExternalDraft });
      }
    } catch (err: any) {
      notifyToast('error', err.message);
    } finally {
      setSavingIndividualNotes(false);
    }
  };

  const handleSaveIndividualResult = async (targetEventId: string) => {
    if (!individualResultValue) {
      notifyToast('warning', 'Please select an interview status result.');
      return;
    }
    try {
      setSubmittingIndividualResult(true);
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetch(`/api/admin/events/${targetEventId}/result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ result: individualResultValue })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to submit interview result.');
      }
      notifyToast('success', `Interview status updated to "${individualResultValue}" and candidate has been notified.`);
      fetchEvents();
      // Update in-memory focused/slide-in event result
      if (focusedEvent && focusedEvent.eventId === targetEventId) {
        setFocusedEvent({ ...focusedEvent, result: individualResultValue });
      }
      if (slideInEvent && slideInEvent.eventId === targetEventId) {
        setSlideInEvent({ ...slideInEvent, result: individualResultValue });
      }
    } catch (err: any) {
      notifyToast('error', err.message);
    } finally {
      setSubmittingIndividualResult(false);
    }
  };

  const handleExportRoster = (ev: Event) => {
    const slots = ev.slots || [];
    const headers = [
      'Appointment / Booking ID',
      'Applicant Name',
      'Applicant Email',
      'Booked Time / Slot',
      'Date',
      'Program',
      'Booking Status',
      'Meeting Link'
    ];

    // Wave 5 (M-05): shared csvCell — quoting + formula-injection guard.
    const escapeCsv = (val: any) => csvCell(val);

    const eventDateStr = ev.dateTime ? (new Date(ev.dateTime).toLocaleDateString() !== 'Invalid Date' ? new Date(ev.dateTime).toLocaleDateString() : ev.dateTime) : '';

    const rows = slots.map(s => {
      const bBy = (s.bookedBy || {}) as { name?: string; email?: string; applicationId?: string };
      const appId = bBy.applicationId || '';
      const appObj = appId ? applications.find(a => a.applicationId === appId) : undefined;
      const programName = appObj ? (appObj.programType || (appObj as any).programName || appObj.programId || '') : '';

      return [
        s.id || '',
        bBy.name || '',
        bBy.email || '',
        `${s.startTime} - ${s.endTime}`,
        eventDateStr,
        programName,
        s.bookingStatus || (bBy.name ? 'booked' : 'available'),
        s.meetingUrl || ev.meetingUrl || ''
      ];
    });

    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...rows.map(r => r.map(escapeCsv).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${ev.title.replace(/[^a-zA-Z0-9]/g, '_')}_roster.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── Export modal state ──
  const [showExportModal, setShowExportModal] = useState(false);
  // ── Scheduling invite modal state ──
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [schedulingAgentId, setSchedulingAgentId] = useState('');
  const [schedulingAgentName, setSchedulingAgentName] = useState('');
  const [schedulingDuration, setSchedulingDuration] = useState(30);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ success: boolean; url?: string } | null>(null);
  useBodyScrollLock(!!isCreatingEvent || showExportModal);

  // ── Modal stacking layers for the five overlays in this view ──
  const dateZ = useModalLayer(!!datePopupDate);
  const EVENT_EXPORT_FIELDS = [
    { id: 'title', label: 'Title' },
    { id: 'eventType', label: 'Type' },
    { id: 'dateTime', label: 'Date/Time' },
    { id: 'location', label: 'Location' },
    { id: 'description', label: 'Description' },
    { id: 'status', label: 'Status' },
    { id: 'applicantId', label: 'Applicant ID' },
    { id: 'createdAt', label: 'Created At' },
  ];
  const [selectedExportFields, setSelectedExportFields] = useState<string[]>(EVENT_EXPORT_FIELDS.map(f => f.id));

  const handleExportEvents = () => {
    const fields = EVENT_EXPORT_FIELDS.filter(f => selectedExportFields.includes(f.id));
    const header = fields.map(f => f.label).join(',');
    const rows = filteredEvents.map(ev => {
      return fields.map(f => {
        let val = (ev as any)[f.id] || '';
        if (f.id === 'dateTime' && ev.dateTime) val = new Date(ev.dateTime).toLocaleString();
        if (f.id === 'createdAt' && ev.createdAt) val = new Date(ev.createdAt).toLocaleString();
        // Wave 5 (M-05): shared csvCell — quoting + formula-injection guard.
        return csvCell(val);
      }).join(',');
    }).join('\n');
    const csv = header + '\n' + rows;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `events_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportModal(false);
  };

  return (
    <div className="space-y-8 animate-fade-in text-slate-800">
    {/* CENTRALIZED EVENTS HEADER */}
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-4">
    <div>
    <h1 className="text-xl font-extrabold font-display tracking-tight text-slate-900">Events & Placements Scheduling</h1>
    </div>
    <div className="flex items-center space-x-2">
      <button
        onClick={() => setShowExportModal(true)}
        className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 cursor-pointer self-start"
      >
        <Download className="h-4 w-4" />
        <span>Export</span>
      </button>
      <button
        onClick={() => { setShowSchedulingModal(true); setInviteResult(null); setSchedulingAgentId(''); setSchedulingAgentName(''); setSchedulingDuration(30); }}
        className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition-all flex items-center space-x-1.5 cursor-pointer self-start"
      >
        <Calendar className="h-4 w-4" />
        <span>Invite for Event</span>
      </button>
    {!isCreatingEvent && canEdit && (
    <div className="flex gap-2">
      <button
      onClick={() => {
      setIsCreatingEvent(true);
      setIsEditingEventId(null);
      setIsGroupEventForm(true);
      setEventTitle('');
      setEventType('Interview');
      setEventDate('');
      setEventStartTime('09:00');
      setEventEndTime('17:00');
      setSessionDuration(15);
      setBufferBefore(0);
      setBufferAfter(0);
      setBlockedTimes([]);
      setBookingOpensType('immediately');
      setBookingOpensAt('');
      setBookingClosesType('hoursBefore');
      setBookingClosesHours(24);
      setBookingClosesAt('');
      setEventLocation('');
      setEventDescription('');
      setEventStatus('Scheduled');
      setIsVirtualMeeting(false);
      setEligibleProgramId('all');
      setEligibleTerm('all');
      setEligibleStage('all');
      setEligibleStatus('all');
      setGroupAssignedUids([]);
      }}
      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-90 rounded-xl text-xs font-bold transition-all shadow-md flex items-center space-x-1.5 cursor-pointer"
      >
      <Plus className="h-4 w-4" />
      <span>New Group Event</span>
      </button>

      <button
      onClick={() => {
      setIsCreatingEvent(true);
      setIsEditingEventId(null);
      setIsGroupEventForm(false);
      setEventTitle('');
      setEventType('Interview');
      setEventDateTime('');
      setEventLocation('');
      setEventDescription('');
      setEventStatus('Scheduled');
      if (applications.length > 0) {
      setEventApplicantId(applications[0].applicationId);
      } else {
      setEventApplicantId('');
      }
      }}
      className="px-4 py-2 btn-primary  text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-500/20 flex items-center space-x-1.5 cursor-pointer"
      >
      <Plus className="h-4 w-4" />
      <span>New Event</span>
      </button>
    </div>
    )}
    </div>
    </div>

    {/* SEARCH AND ADVANCED FILTERS BAR */}
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4 animate-fade-in text-slate-800">
    <div className="flex flex-col lg:flex-row gap-3 items-stretch justify-between">
    {/* Search query input */}
    <div className="relative flex-1">
    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
    <input
    type="search"
    value={eventSearchQuery}
    onChange={(e) => setEventSearchQuery(e.target.value)}
    placeholder="Search events by title, type, location, or applicant name..."
    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-10 py-1.5 text-xs outline-none focus:bg-white focus:border-brand font-medium text-slate-800 transition-all font-display"
    />
    {eventSearchQuery && (
    <button aria-label="Clear search" title="Clear search"
    onClick={() => setEventSearchQuery('')}
    className="absolute right-3 top-2 h-5 w-5 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-full flex items-center justify-center text-xs transition-transform cursor-pointer"
    >
    <X className="h-3 w-3" />
    </button>
    )}
    </div>

    {/* Toggle control viewMode */}
    <div className="flex items-center space-x-1 border border-slate-200 bg-slate-50 p-1 rounded-xl shrink-0">
    <button
    onClick={() => setEventViewMode('calendar')}
    className={`px-3 py-1 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
    eventViewMode === 'calendar' ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-950'
    }`}
    >
    <Calendar className="h-3.5 w-3.5" />
    <span>Calendar View</span>
    </button>
    <button
    onClick={() => setEventViewMode('list')}
    className={`px-3 py-1 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
    eventViewMode === 'list' ? 'bg-white text-brand shadow-sm' : 'text-slate-600 hover:text-slate-950'
    }`}
    >
    <List className="h-3.5 w-3.5" />
    <span>List View</span>
    </button>
    </div>
    </div>

    {/* Advanced filter dropdowns row */}
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 pt-2 text-slate-700">
    {/* 1. Event Type filter */}
    <div className="space-y-1">
    <label className="text-caption uppercase font-bold text-slate-500 tracking-wider">Event Type</label>
    <select
    value={filterEventType}
    onChange={(e) => setFilterEventType(e.target.value)}
    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:bg-white focus:border-brand font-bold"
    >
    <option value="All">All Types</option>
    <option value="Embassy">Embassy</option>
    <option value="Interview">Interview</option>
    <option value="Orientation">Orientation</option>
    <option value="Training">Training</option>
    <option value="Flight">Flight</option>
    <option value="Medical">Medical</option>
    <option value="Other">Other</option>
    </select>
    </div>

    {/* 2. Status filter */}
    <div className="space-y-1">
    <label className="text-caption uppercase font-bold text-slate-500 tracking-wider">Event Status</label>
    <select
    value={filterEventStatus}
    onChange={(e) => setFilterEventStatus(e.target.value)}
    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:bg-white focus:border-brand font-bold"
    >
    <option value="All">All Statuses</option>
    <option value="Scheduled">Scheduled</option>
    <option value="Pending">Pending</option>
    <option value="Awaiting Response">Awaiting Response</option>
    <option value="Confirmed">Confirmed</option>
    <option value="Rescheduled">Rescheduled</option>
    <option value="Completed">Completed</option>
    <option value="Cancelled">Cancelled</option>
    <option value="Declined">Declined</option>
    </select>
    </div>

    {/* 3. Applicant filter */}
    <div className="space-y-1 text-left min-w-[200px]">
    <label className="text-caption uppercase font-bold text-slate-500 tracking-wider">Exchange Candidate</label>
    <SearchableSelect
      value={filterEventApplicant || 'All'}
      onChange={(val) => setFilterEventApplicant(val)}
      options={[
        { value: 'All', label: 'All Candidates' },
        ...Array.from(new Map(applications.map(a => [a.applicationId || a.id, a])).values()).map(app => ({
          value: app.applicationId || app.id,
          label: getSafeApplicantName(app),
          hint: `${app.applicationId || ''} · ${app.email || ''}`.trim(),
          tag: app.programType || undefined
        }))
      ]}
      placeholder="Search candidate..."
      allowClear={false}
    />
    </div>

    {/* 4. Date ranges presets */}
    <div className="space-y-1">
    <label className="text-caption uppercase font-bold text-slate-500 tracking-wider">Timeline Presets</label>
    <select
    value={filterDatePreset}
    onChange={(e) => {
    setFilterDatePreset(e.target.value);
    if (e.target.value !== 'All Time') {
    setFilterDateStart('');
    setFilterDateEnd('');
    }
    }}
    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:bg-white focus:border-brand font-bold"
    >
    <option value="All Time">All Time</option>
    <option value="This Week">This Week</option>
    <option value="This Month">This Month</option>
    <option value="Next 30 Days">Next 30 Days</option>
    </select>
    </div>

    {/* 5. Custom Start Date */}
    <div className="space-y-1">
    <label className="text-caption uppercase font-bold text-slate-500 tracking-wider">Custom Start Date</label>
    <input
    type="date"
    value={filterDateStart}
    onChange={(e) => {
    setFilterDateStart(e.target.value);
    setFilterDatePreset('All Time'); // override preset
    }}
    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-xs outline-none focus:bg-white focus:border-brand font-semibold"
    />
    </div>

    {/* 6. Custom End Date */}
    <div className="space-y-1">
    <label className="text-caption uppercase font-bold text-slate-500 tracking-wider">Custom End Date</label>
    <input
    type="date"
    value={filterDateEnd}
    onChange={(e) => {
    setFilterDateEnd(e.target.value);
    setFilterDatePreset('All Time'); // override preset
    }}
    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-xs outline-none focus:bg-white focus:border-brand font-semibold"
    />
    </div>
    </div>

    {/* Reset trigger bar */}
    {(eventSearchQuery || filterEventType !== 'All' || filterEventStatus !== 'All' || filterEventApplicant !== 'All' || filterDateStart || filterDateEnd || filterDatePreset !== 'All Time') && (
    <div className="flex items-center justify-between pt-1 border-t border-slate-100 text-xs">
    <span className="text-caption text-slate-500 font-medium">
    Showing <strong className="text-brand">{displayedEvents.length}</strong> of <strong>{events.length}</strong> scheduled events (page {eventsPage} of {eventsTotalPages})
    </span>
    <button
    onClick={() => {
    setEventSearchQuery('');
    setFilterEventType('All');
    setFilterEventStatus('All');
    setFilterEventApplicant('All');
    setFilterDateStart('');
    setFilterDateEnd('');
    setFilterDatePreset('All Time');
    }}
    className="text-xs font-bold text-brand hover:text-blue-800 transition-colors flex items-center space-x-1 cursor-pointer"
    >
    <RefreshCw className="h-3 w-3" />
    <span>Clear All Filters</span>
    </button>
    </div>
    )}
    </div>

    {/* EVENT CREATION / EDITING MODAL OVERLAY */}
    {(isCreatingEvent || isEditingEventId) && (
    <Modal onClose={() => { setIsCreatingEvent(false); setIsEditingEventId(null); }} title={isEditingEventId ? 'Edit event' : 'Create event'}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in" onClick={e => e.stopPropagation()}>
    {/* Header */}
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 shrink-0">
    <div className="flex items-center justify-between">
    <div className="flex items-center space-x-3">
    <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
    {isEditingEventId ? <Edit3 className="h-4 w-4 text-white" /> : <Plus className="h-4 w-4 text-white" />}
    </div>
    <div>
    <h3 className="text-sm font-bold text-white font-display">
      {isGroupEventForm
        ? (isEditingEventId ? "Modify Group Event" : "Establish New Group Event")
        : (isEditingEventId ? "Modify Existing Event" : "Establish New Event")}
    </h3>
    <p className="text-caption text-blue-100 mt-0.5">
      {isGroupEventForm
        ? "Configure timings, slot parameters, breaks, and booking rules"
        : (isEditingEventId ? 'Update event details below' : 'Fill in the details to create a new event')}
    </p>
    </div>
    </div>
    <button
    onClick={() => { setIsCreatingEvent(false); setIsEditingEventId(null); }}
    className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition-all cursor-pointer"
    >
    <X className="h-5 w-5" />
    </button>
    </div>
    </div>
    {/* Body */}
    <div className="p-5 space-y-4 overflow-y-auto flex-1">

    {/* CONDITIONAL RENDERING: GROUP FORM VS STANDARD FORM */}
    {isGroupEventForm ? (
      <div className="space-y-4 text-xs text-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Title */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Group Event Name *</label>
            <input
              type="text"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:bg-white focus:border-brand font-semibold text-slate-800"
              placeholder="e.g. Autumn Matching Roster"
              value={eventTitle}
              onChange={(e) => setEventTitle(e.target.value)}
            />
          </div>

          {/* Type */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Event Category *</label>
            <select
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:bg-white focus:border-brand font-bold text-slate-700"
              value={eventType}
              onChange={(e) => setEventType(e.target.value as any)}
            >
              <option value="Interview">Interview Session</option>
              <option value="Orientation">Orientation / Assembly</option>
              <option value="Training">Training / Placement Prep</option>
              <option value="Medical">Medical Checkup</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Date */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Event Date *</label>
            <input
              type="date"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:bg-white focus:border-brand font-mono font-bold text-slate-700"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
            />
          </div>

          {/* Time Zone */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Time Zone *</label>
            <select
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:bg-white focus:border-brand font-bold text-slate-700"
              value={eventTimezone}
              onChange={(e) => setEventTimezone(e.target.value)}
            >
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          {/* Start Time */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Event Start Time *</label>
            <input
              type="time"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:bg-white focus:border-brand font-mono font-bold text-slate-700"
              value={eventStartTime}
              onChange={(e) => setEventStartTime(e.target.value)}
            />
          </div>

          {/* End Time */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Event End Time *</label>
            <input
              type="time"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:bg-white focus:border-brand font-mono font-bold text-slate-700"
              value={eventEndTime}
              onChange={(e) => setEventEndTime(e.target.value)}
            />
          </div>

          {/* Meeting Type & Video Platform Selector for Group Events */}
          <div className="md:col-span-2 space-y-2">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block">
              Meeting Type & Video Platform *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => {
                  setIsVirtualMeeting(false);
                  setEventLocation('');
                }}
                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                  !isVirtualMeeting && !eventLocation.includes('meet.jit.si') && !eventLocation.includes('daily.co')
                    ? 'bg-brand text-white border-brand shadow-sm'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <MapPin className="h-3.5 w-3.5" />
                <span>In-Person</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsVirtualMeeting(true);
                  setVideoProvider('jitsi');
                  setEventLocation('Jitsi Meeting Room (auto-generated per slot)');
                }}
                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                  isVirtualMeeting && videoProvider === 'jitsi'
                    ? 'bg-brand text-white border-brand shadow-sm'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <Video className="h-3.5 w-3.5" />
                <span>Jitsi Meet</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsVirtualMeeting(true);
                  setVideoProvider('daily');
                  setEventLocation('Daily.co Room (auto-generated per slot)');
                }}
                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                  isVirtualMeeting && videoProvider === 'daily'
                    ? 'bg-brand text-white border-brand shadow-sm'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <Video className="h-3.5 w-3.5" />
                <span>Daily.co Video</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsVirtualMeeting(false);
                  setEventLocation('https://');
                }}
                className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                  !isVirtualMeeting && (eventLocation.startsWith('http://') || eventLocation.startsWith('https://'))
                    ? 'bg-brand text-white border-brand shadow-sm'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span>Custom URL</span>
              </button>
            </div>

            {isVirtualMeeting ? (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-medium flex items-center gap-2">
                <Video className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>
                  {videoProvider === 'daily'
                    ? 'A unique Daily.co room will be created automatically per slot using workspace settings.'
                    : 'A unique Jitsi room will be created automatically per slot on booking.'}
                </span>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={eventLocation}
                  onChange={(e) => setEventLocation(e.target.value)}
                  placeholder="e.g. 123 Main St, New York or Google Meet URL"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium outline-none focus:bg-white focus:border-brand"
                />
              </div>
            )}
          </div>

          {/* Assign to Workspace Team (Admins / Staff) — same agent pool as
              regular single-applicant events; assigned staff see this group
              event on their own event dashboards. */}
          <div className="md:col-span-2 space-y-1.5">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Assign To (Workspace Team / Admins){agentsError && (
              <button type="button" onClick={fetchAgents} className="ml-2 text-[10px] font-bold text-rose-500 hover:text-rose-700 underline cursor-pointer">Retry</button>
            )}</label>
            {agentsError ? (
              <div className="w-full bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs font-semibold text-rose-600 flex items-center justify-between">
                Failed to load team members
                <button onClick={fetchAgents} className="underline cursor-pointer font-bold">Retry</button>
              </div>
            ) : !agentsLoaded ? (
              <div className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold text-slate-400 flex items-center gap-2">
                <Loader className="h-3.5 w-3.5 animate-spin" /> Loading team members...
              </div>
            ) : agentsList.length === 0 ? (
              <div className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-semibold text-slate-400">No team members found</div>
            ) : (
              <div className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2 grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-44 overflow-y-auto">
                {agentsList.map(agent => {
                  const checked = groupAssignedUids.includes(agent.uid);
                  return (
                    <label key={agent.uid} className={`flex items-start gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer transition-all border ${checked ? 'bg-blue-50 border-blue-200 text-blue-900' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                      <input
                        type="checkbox"
                        className="mt-0.5 accent-blue-600 cursor-pointer"
                        checked={checked}
                        onChange={() => {
                          setGroupAssignedUids(prev => checked ? prev.filter(id => id !== agent.uid) : [...prev, agent.uid]);
                        }}
                      />
                      <span className="leading-tight">
                        <span className="block font-bold">{agent.name}</span>
                        <span className="block text-[11px] text-slate-500">{agent.email}{agent.role ? ` · ${agent.role}` : ''}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-slate-500 font-medium">Select the admins/staff who will run this group event — they will see it on their own event dashboards.</p>
          </div>

          {/* Status */}
          <div className="space-y-1.5 md:col-span-2">
            <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Event Status *</label>
            <select
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:bg-white focus:border-brand font-bold text-slate-700"
              value={eventStatus}
              onChange={(e) => setEventStatus(e.target.value as any)}
            >
              <option value="Scheduled">Scheduled</option>
              <option value="Pending">Pending</option>
              <option value="Awaiting Response">Awaiting Response</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Rescheduled">Rescheduled</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Declined">Declined</option>
            </select>
          </div>
        </div>

        {/* External Notes */}
        <div className="space-y-1.5">
          <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500">External Notes (Visible to Applicants - Markdown Supported)</label>
          <div className="relative">
            <textarea
              rows={3}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:bg-white focus:border-brand font-medium text-xs"
              placeholder="Specify dress code, documents to carry, pre-reads, etc."
              value={eventExternalNotes}
              onChange={(e) => {
                setEventExternalNotes(e.target.value);
                setEventDescription(e.target.value);
              }}
            />
            <div className="absolute top-1 right-1">
              <ImproveTextDropdown
                getValue={() => eventExternalNotes}
                onImprove={(v) => {
                  setEventExternalNotes(v);
                  setEventDescription(v);
                }}
                isHtml={false}
                context="group event details"
              />
            </div>
          </div>
        </div>

        {/* Internal Notes (Rich Text Editor for Group Interviews) */}
        <div className="space-y-1.5">
          <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 font-bold text-slate-700">Internal Notes (Confidential - Admins Only - Rich Text Editor)</label>
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <RichTextEditor
              value={eventInternalNotes}
              onChange={(html) => setEventInternalNotes(html)}
              placeholder="Provide interview questions, key prompts, rating guidelines, and confidential candidate notes..."
            />
          </div>
        </div>

        {/* TARGET ELIGIBILITY REQUIREMENTS */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <h4 className="text-[10px] uppercase font-black text-slate-400 tracking-wider flex items-center gap-1.5">
            <Shield className="h-3.5 w-3.5 text-brand" />
            Target Eligibility Requirements
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            {/* Program Constraint */}
            <div className="space-y-1">
              <label className="font-bold text-slate-600 block">Restricted to Program</label>
              <SearchableSelect
                value={eligibleProgramId === 'all' ? '' : eligibleProgramId}
                onChange={(v) => setEligibleProgramId(v || 'all')}
                options={programs.map(p => ({
                  value: p.id,
                  label: p.name || p.code || p.id,
                }))}
                placeholder="Search programs…"
                clearLabel="All Programs (No restriction)"
                emptyLabel="No programs found"
              />
            </div>

            {/* Term Constraint */}
            <div className="space-y-1">
              <label className="font-bold text-slate-600 block">Restricted to Term/Season</label>
              <select
                className="w-full border border-slate-200 rounded-lg bg-white p-1.5 font-semibold text-slate-800"
                value={eligibleTerm}
                onChange={(e) => setEligibleTerm(e.target.value)}
              >
                <option value="all">All Terms (No restriction)</option>
                {uniqueTerms.map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
              </select>
            </div>

            {/* Stage Constraint */}
            <div className="space-y-1">
              <label className="font-bold text-slate-600 block">Restricted to Pipeline Stage</label>
              <select
                className="w-full border border-slate-200 rounded-lg bg-white p-1.5 font-semibold text-slate-800"
                value={eligibleStage}
                onChange={(e) => setEligibleStage(e.target.value)}
              >
                <option value="all">All Stages (No restriction)</option>
                {Array.from({ length: 10 }, (_, i) => i + 1).map(stage => (
                  <option key={stage} value={String(stage)}>Stage {stage}</option>
                ))}
              </select>
            </div>

            {/* Status Constraint */}
            <div className="space-y-1">
              <label className="font-bold text-slate-600 block">Restricted to Application Status</label>
              <select
                className="w-full border border-slate-200 rounded-lg bg-white p-1.5 font-semibold text-slate-800"
                value={eligibleStatus}
                onChange={(e) => setEligibleStatus(e.target.value)}
              >
                <option value="all">All Statuses (No restriction)</option>
                <option value="Draft">Draft</option>
                <option value="Submitted">Submitted</option>
                <option value="Under Review">Under Review</option>
                <option value="Approved">Approved</option>
                <option value="Returned">Returned</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Access & Approval Options */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <h4 className="text-[10px] uppercase font-black text-slate-400 tracking-wider">Access & Approval Rules</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-start gap-2.5 p-2 bg-white border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
              <input
                type="checkbox"
                checked={publicNoInvite}
                onChange={(e) => setPublicNoInvite(e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-brand"
              />
              <div>
                <span className="font-bold text-slate-800 text-xs block">Public Event (No Invitation Required)</span>
                <span className="text-[11px] text-slate-500 font-medium leading-snug block">Applicants can access the public booking link directly without needing an email invitation.</span>
              </div>
            </label>

            <label className="flex items-start gap-2.5 p-2 bg-white border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
              <input
                type="checkbox"
                checked={approvalRequired}
                onChange={(e) => setApprovalRequired(e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-brand"
              />
              <div>
                <span className="font-bold text-slate-800 text-xs block">Requires Admin Approval</span>
                <span className="text-[11px] text-slate-500 font-medium leading-snug block">Submitted bookings remain pending until reviewed and approved by an administrator.</span>
              </div>
            </label>
          </div>
        </div>

        {/* Slot Parameters */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <h4 className="text-[10px] uppercase font-black text-slate-400 tracking-wider">Slot Configuration</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="font-bold text-slate-600">Session Duration (min)</label>
              <select
                className="w-full border border-slate-200 rounded-lg bg-white p-1.5 font-semibold text-slate-800"
                value={sessionDuration}
                onChange={(e) => setSessionDuration(Number(e.target.value))}
              >
                <option value={10}>10 min</option>
                <option value={15}>15 min</option>
                <option value={20}>20 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="font-bold text-slate-600">Buffer Before (min)</label>
              <input
                type="number"
                min={0}
                className="w-full border border-slate-200 rounded-lg bg-white p-1.5 font-semibold text-slate-800"
                value={bufferBefore}
                onChange={(e) => setBufferBefore(Number(e.target.value))}
              />
            </div>
            <div className="space-y-1">
              <label className="font-bold text-slate-600">Buffer After (min)</label>
              <input
                type="number"
                min={0}
                className="w-full border border-slate-200 rounded-lg bg-white p-1.5 font-semibold text-slate-800"
                value={bufferAfter}
                onChange={(e) => setBufferAfter(Number(e.target.value))}
              />
            </div>
          </div>
        </div>

        {/* Blocked Times */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <h4 className="text-[10px] uppercase font-black text-slate-400 tracking-wider">Blocked Periods & Breaks</h4>

          {/* Add block inputs */}
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-1">
              <label className="font-bold text-slate-600">Label</label>
              <input
                type="text"
                className="w-full border border-slate-200 rounded-lg bg-white p-1.5"
                placeholder="e.g. Lunch Break"
                value={newBlockLabel}
                onChange={(e) => setNewBlockLabel(e.target.value)}
              />
            </div>
            <div className="w-24 space-y-1">
              <label className="font-bold text-slate-600">Start Time</label>
              <input
                type="time"
                className="w-full border border-slate-200 rounded-lg bg-white p-1"
                value={newBlockStart}
                onChange={(e) => setNewBlockStart(e.target.value)}
              />
            </div>
            <div className="w-24 space-y-1">
              <label className="font-bold text-slate-600">End Time</label>
              <input
                type="time"
                className="w-full border border-slate-200 rounded-lg bg-white p-1"
                value={newBlockEnd}
                onChange={(e) => setNewBlockEnd(e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                if (!newBlockLabel.trim()) return;
                const newBlock: BlockedPeriod = {
                  id: crypto.randomUUID(),
                  startTime: newBlockStart,
                  endTime: newBlockEnd,
                  label: newBlockLabel.trim()
                };
                setBlockedTimes([...blockedTimes, newBlock]);
                setNewBlockLabel('');
              }}
              className="p-1.5 bg-blue-600 text-white rounded-lg font-bold"
            >
              Add Block
            </button>
          </div>

          {/* Current blocks list */}
          {blockedTimes.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-slate-200">
              {blockedTimes.map(block => (
                <div key={block.id} className="flex justify-between items-center bg-white px-3 py-1.5 border border-slate-200 rounded-lg">
                  <span className="font-bold text-slate-800">{block.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-slate-500 font-bold bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">
                      {block.startTime} - {block.endTime}
                    </span>
                    <button
                      type="button"
                      onClick={() => setBlockedTimes(blockedTimes.filter(b => b.id !== block.id))}
                      className="text-rose-500 hover:text-rose-700 p-0.5"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Booking Rules */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <h4 className="text-[10px] uppercase font-black text-slate-400 tracking-wider">Booking Rules</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Booking Opens */}
            <div className="space-y-2">
              <label className="font-bold text-slate-600 block">Booking Opens</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setBookingOpensType('immediately')}
                  className={`px-3 py-1 border rounded-lg font-bold ${bookingOpensType === 'immediately' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  Immediately
                </button>
                <button
                  type="button"
                  onClick={() => setBookingOpensType('scheduled')}
                  className={`px-3 py-1 border rounded-lg font-bold ${bookingOpensType === 'scheduled' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  Scheduled
                </button>
              </div>
              {bookingOpensType === 'scheduled' && (
                <input
                  type="datetime-local"
                  value={bookingOpensAt}
                  onChange={(e) => setBookingOpensAt(e.target.value)}
                  className="w-full border border-slate-200 bg-white p-1.5 rounded-lg mt-1 font-mono font-bold"
                />
              )}
            </div>

            {/* Booking Closes */}
            <div className="space-y-2">
              <label className="font-bold text-slate-600 block">Booking Closes</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setBookingClosesType('hoursBefore')}
                  className={`px-3 py-1 border rounded-lg font-bold ${bookingClosesType === 'hoursBefore' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  Hours Before Event
                </button>
                <button
                  type="button"
                  onClick={() => setBookingClosesType('scheduled')}
                  className={`px-3 py-1 border rounded-lg font-bold ${bookingClosesType === 'scheduled' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}
                >
                  Specific Date
                </button>
              </div>
              {bookingClosesType === 'hoursBefore' && (
                <div className="flex items-center gap-1.5 mt-1">
                  <input
                    type="number"
                    min={1}
                    className="w-20 border border-slate-200 bg-white p-1.5 rounded-lg font-bold text-slate-800"
                    value={bookingClosesHours}
                    onChange={(e) => setBookingClosesHours(Number(e.target.value))}
                  />
                  <span className="font-semibold text-slate-500 text-[11px]">hours before start</span>
                </div>
              )}
              {bookingClosesType === 'scheduled' && (
                <input
                  type="datetime-local"
                  value={bookingClosesAt}
                  onChange={(e) => setBookingClosesAt(e.target.value)}
                  className="w-full border border-slate-200 bg-white p-1.5 rounded-lg mt-1 font-mono font-bold"
                />
              )}
            </div>
          </div>
        </div>

        {/* Live Slots Preview Grid */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
          <div className="flex justify-between items-center">
            <h4 className="text-[10px] uppercase font-black text-slate-400 tracking-wider">Live Slots Preview</h4>
            <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-0.5 rounded-lg">
              {generateSlotsPreview().length} slots generated
            </span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-36 overflow-y-auto pt-1">
            {generateSlotsPreview().map((s, idx) => (
              <div key={s.id} className="bg-white border border-slate-200 px-2 py-1 rounded-md text-center font-mono text-[10px] font-semibold text-slate-600">
                {s.startTime}
              </div>
            ))}
          </div>
        </div>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Title */}
      <div className="space-y-1.5">
      <label className="text-caption uppercase font-bold tracking-wider text-slate-500">Event Title *</label>
      <input
      type="search"
      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-medium"
      placeholder="e.g. Host Employer Matching Interview"
      value={eventTitle}
      onChange={(e) => setEventTitle(e.target.value)}
      />
      </div>

      {/* Type */}
      <div className="space-y-1.5">
      <label className="text-caption uppercase font-bold tracking-wider text-slate-500">Event Type *</label>
      <select
      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-bold text-slate-700"
      value={eventType}
      onChange={(e) => setEventType(e.target.value as any)}
      >
      <option value="Embassy">Embassy Appointment</option>
      <option value="Interview">Interview Session</option>
      <option value="Orientation">Orientation / Assembly</option>
      <option value="Training">Training / Placement Prep</option>
      <option value="Flight">Flight & Departure Coordination</option>
      <option value="Medical">Medical Checkup</option>
      <option value="Other">Other</option>
      </select>
      </div>

      {/* Date & Time */}
      <div className="space-y-1.5">
      <label className="text-caption uppercase font-bold tracking-wider text-slate-500">Date & Time *</label>
      <input
      type="datetime-local"
      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-mono font-bold text-slate-700"
      value={eventDateTime}
      onChange={(e) => setEventDateTime(e.target.value)}
      />
      </div>

      {/* Time Zone */}
      <div className="space-y-1.5">
      <label className="text-caption uppercase font-bold tracking-wider text-slate-500">Time Zone *</label>
      <select
      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-bold text-slate-700"
      value={eventTimezone}
      onChange={(e) => setEventTimezone(e.target.value)}
      >
      {COMMON_TIMEZONES.map(tz => (
        <option key={tz} value={tz}>{tz}</option>
      ))}
      </select>
      </div>

      {/* Meeting Type & Video Platform Selector for Single Event */}
      <div className="md:col-span-2 space-y-2">
        <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block">
          Meeting Type & Video Platform *
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            type="button"
            onClick={() => {
              setIsVirtualMeeting(false);
              setEventLocation('');
            }}
            className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
              !isVirtualMeeting && !eventLocation.includes('meet.jit.si') && !eventLocation.includes('daily.co')
                ? 'bg-brand text-white border-brand shadow-sm'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <MapPin className="h-3.5 w-3.5" />
            <span>In-Person</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setIsVirtualMeeting(true);
              setVideoProvider('jitsi');
              setEventLocation('Jitsi Meeting Room (auto-generated)');
            }}
            className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
              isVirtualMeeting && videoProvider === 'jitsi'
                ? 'bg-brand text-white border-brand shadow-sm'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <Video className="h-3.5 w-3.5" />
            <span>Jitsi Meet</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setIsVirtualMeeting(true);
              setVideoProvider('daily');
              setEventLocation('Daily.co Room (auto-generated)');
            }}
            className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
              isVirtualMeeting && videoProvider === 'daily'
                ? 'bg-brand text-white border-brand shadow-sm'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <Video className="h-3.5 w-3.5" />
            <span>Daily.co Video</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setIsVirtualMeeting(false);
              setEventLocation('https://');
            }}
            className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
              !isVirtualMeeting && (eventLocation.startsWith('http://') || eventLocation.startsWith('https://'))
                ? 'bg-brand text-white border-brand shadow-sm'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
            }`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span>Custom URL</span>
          </button>
        </div>

        {isVirtualMeeting ? (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-medium flex items-center gap-2">
            <Video className="h-4 w-4 text-emerald-600 shrink-0" />
            <span>
              {videoProvider === 'daily'
                ? 'A unique Daily.co room will be created automatically on save using workspace settings.'
                : 'A unique Jitsi room will be created automatically on save.'}
            </span>
          </div>
        ) : (
          <div>
            <input
              type="text"
              value={eventLocation}
              onChange={(e) => setEventLocation(e.target.value)}
              placeholder="e.g. 123 Main St, New York or Google Meet URL"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium outline-none focus:bg-white focus:border-brand"
            />
          </div>
        )}
      </div>

      {/* Assigned Applicant */}
      <div className="space-y-1.5">
      <label className="text-caption uppercase font-bold tracking-wider text-slate-500">Assigned Exchange Applicant *</label>
      <SearchableSelect
      value={eventApplicantId}
      onChange={(v) => setEventApplicantId(v)}
      options={applications.map(app => ({
        value: app.applicationId,
        label: `${getSafeApplicantName(app)} (${app.programType}) - ${app.userEmail}`,
        hint: app.applicationId ? `Applicant ID: ${app.applicationId}` : undefined,
      }))}
      placeholder="Search applicants…"
      clearLabel="-- Choose Candidate --"
      emptyLabel="No applicants found"
      />
      </div>

      {/* Assign to Agent */}
      <div className="space-y-1.5">
      <label className="text-caption uppercase font-bold tracking-wider text-slate-500">Assign To (Agent / Staff){agentsError && (
        <button type="button" onClick={fetchAgents} className="ml-2 text-[10px] font-bold text-rose-500 hover:text-rose-700 underline cursor-pointer">Retry</button>
      )}</label>
      <SearchableSelect
      value={eventAssignedTo ? `${eventAssignedTo}|${eventAssignedToName}` : ''}
      onChange={(nextValue) => {
        const idx = nextValue.indexOf('|');
        if (idx > 0) {
          setEventAssignedTo(nextValue.slice(0, idx));
          setEventAssignedToName(nextValue.slice(idx + 1));
        } else {
          setEventAssignedTo('');
          setEventAssignedToName('');
        }
      }}
      options={agentsList.map(agent => ({
        value: `${agent.uid}|${agent.name}`,
        label: `${agent.name} (${agent.email})${agent.role ? ` {${agent.role}}` : ''}`,
      }))}
      placeholder="Search agents…"
      clearLabel="-- Not assigned --"
      emptyLabel={agentsError ? 'Failed to load agents — click Retry above' : !agentsLoaded ? 'Loading agents...' : 'No agents found'}
      />
      <p className="text-[11px] text-slate-500 font-medium mt-0.5">Events can be assigned to any admin, referrer, or staff member.</p>
      </div>

      {/* Status */}
      <div className="space-y-1.5">
      <label className="text-caption uppercase font-bold tracking-wider text-slate-500">Event Status *</label>
      <select
      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-bold text-slate-700"
      value={eventStatus}
      onChange={(e) => setEventStatus(e.target.value as any)}
      >
      <option value="Scheduled">Scheduled</option>
      <option value="Pending">Pending</option>
      <option value="Awaiting Response">Awaiting Response</option>
      <option value="Confirmed">Confirmed</option>
      <option value="Rescheduled">Rescheduled</option>
      <option value="Completed">Completed</option>
      <option value="Cancelled">Cancelled</option>
      <option value="Declined">Declined</option>
      </select>
      </div>

      {/* External Notes */}
      <div className="space-y-1.5 md:col-span-2">
      <label className="text-caption uppercase font-bold tracking-wider font-bold">External Notes (Visible to Applicants)</label>
      <div className="relative">
      <textarea
      rows={3}
      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-medium"
      placeholder="Specify dress code, documents to carry, pre-reads, etc."
      value={eventExternalNotes}
      onChange={(e) => {
        setEventExternalNotes(e.target.value);
        setEventDescription(e.target.value);
      }}
      />
      <div className="absolute top-1 right-1">
      <ImproveTextDropdown
      getValue={() => eventExternalNotes}
      onImprove={(v) => {
        setEventExternalNotes(v);
        setEventDescription(v);
      }}
      isHtml={false}
      context="event preparation notes"
      />
      </div>
      </div>
      </div>

      {/* Internal Notes */}
      <div className="space-y-1.5 md:col-span-2">
      <label className="text-caption uppercase font-bold tracking-wider font-bold text-slate-700">Internal Notes (Confidential - Admins Only)</label>
      <div className="relative">
      <textarea
      rows={3}
      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-medium"
      placeholder="Confidential interviewer logs, private ratings, and evaluation highlights..."
      value={eventInternalNotes}
      onChange={(e) => setEventInternalNotes(e.target.value)}
      />
      </div>
      </div>

      {/* Notify applicant checkbox */}
      {eventApplicantId && (
      <div className="md:col-span-2 flex items-center space-x-2.5 py-1">
      <input
      type="checkbox"
      id="eventNotifyCheckbox"
      checked={eventNotifyApplicants}
      onChange={(e) => setEventNotifyApplicants(e.target.checked)}
      className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
      />
      <label htmlFor="eventNotifyCheckbox" className="text-xs font-semibold text-slate-600 cursor-pointer select-none">
      Notify applicant of this event
      </label>
      </div>
      )}
      </div>
    )}
    </div>
    {/* Footer */}
    <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 shrink-0">
    <button
    onClick={() => {
    setIsCreatingEvent(false);
    setIsEditingEventId(null);
    }}
    className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer"
    >
    Dismiss
    </button>
    <button
    onClick={isGroupEventForm ? handleSaveGroupEvent : handleSaveEvent}
    disabled={isGroupEventForm ? isSavingGroupEvent : isSavingEvent}
    className="px-4 py-1.5 btn-primary text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
    >
    {(isGroupEventForm ? isSavingGroupEvent : isSavingEvent) && <Loader className="h-3.5 w-3.5 animate-spin" />}
    {(isGroupEventForm ? isSavingGroupEvent : isSavingEvent) ? "Saving..." : (isEditingEventId ? "Save Changes" : "Create Event")}
    </button>
    </div>
    </div>
    </Modal>
    )}

    {/* EVENT VIEW CONTROL RENDERING */}
    {displayedEvents.length === 0 ? (
    <div className="glass-panel p-12 text-center rounded-xl bg-white border border-slate-200 shadow-sm max-w-xl mx-auto space-y-4 animate-fade-in">
    <div className="mx-auto w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-brand">
    <Calendar className="h-6 w-6" />
    </div>
    <div className="space-y-1">
    <h4 className="text-sm font-extrabold text-slate-900">No Events Scheduled Yet</h4>
    <p className="text-xs text-slate-500">There are currently no matching sessions, interviews, visa reviews, or orientation flights registered on the exchange network.</p>
    </div>
    <button
    onClick={() => {
    setIsCreatingEvent(true);
    setIsEditingEventId(null);
    setEventTitle('');
    setEventType('Interview');
    setEventDateTime('');
    setEventLocation('');
    setEventDescription('');
    setEventStatus('Scheduled');
    if (applications.length > 0) {
    setEventApplicantId(applications[0].applicationId);
    } else {
    setEventApplicantId('');
    }
    }}
    className="px-4 py-2 btn-primary  text-white rounded-lg text-xs font-bold transition-all shadow cursor-pointer inline-flex items-center space-x-1.5"
    >
    <Plus className="h-3.5 w-3.5" />
    <span>Establish First Event</span>
    </button>
    </div>
    ) : eventViewMode === 'calendar' ? (
      <SchedulingCalendar
        initialEvents={filteredEvents.map(ev => {
          const startTime = ev.dateTime || new Date().toISOString();
          const startDateObj = new Date(startTime);
          const endDateObj = new Date(startDateObj.getTime() + 60 * 60 * 1000); // default +1 hour duration
          const endTime = endDateObj.toISOString();

          const type = (ev.eventType || '').toLowerCase();
          const calendarId = ['embassy', 'interview', 'orientation', 'training', 'flight', 'medical', 'other'].includes(type)
            ? type
            : 'other';

          const teamNames = Array.isArray(ev.assignedTeam) ? ev.assignedTeam.map((m: any) => m.name || m.userName).filter(Boolean) : [];
          const hostName = ev.assignedToName || (ev.assignedTo ? agentsList.find(a => a.uid === ev.assignedTo)?.name : null) || teamNames[0] || (ev.assignedTo && !ev.assignedTo.startsWith('uid') ? ev.assignedTo : 'Unassigned');

          return {
            id: ev.eventId,
            title: ev.title,
            startTime,
            endTime,
            calendarId,
            status: ev.status,
            assignedTo: hostName,
            location: ev.location,
            description: ev.description,
          };
        })}
        onEventChange={() => {}}
        userRole="admin"
        title="Admin Placement & Interviews Grid"
        agentsList={agentsList}
      />
    ) : (
    /* LIST VIEW */
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden animate-fade-in text-xs text-slate-700">
    <div className="overflow-x-auto">
    <table className="w-full text-left border-collapse">
    <thead>
    <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 font-extrabold uppercase text-caption tracking-wider">
    <th className="py-3 px-5">Date & Time</th>
    <th className="py-3 px-5">Event Detail</th>
    <th className="py-3 px-5">Candidate</th>
    <th className="py-3 px-5">Assigned Admin</th>
    <th className="py-3 px-5">Location</th>
    <th className="py-3 px-5">Status</th>
    <th className="py-3 px-5 text-right">Actions</th>
    </tr>
    </thead>
    <tbody className="divide-y divide-slate-100">
    {displayedEvents.map(ev => {
    const assignedApp = applications.find(a => a.applicationId === ev.applicantId);

    let typeColor = 'bg-slate-50 border-slate-200 text-slate-700';
    let iconElement = <Calendar className="h-3 w-3" />;

    if (ev.eventType === 'Embassy') {
    typeColor = 'bg-blue-50 border-blue-100 text-brand';
    iconElement = <Shield className="h-3 w-3" />;
    } else if (ev.eventType === 'Interview') {
    typeColor = 'bg-blue-50 border-blue-100 text-brand';
    iconElement = <MessageSquare className="h-3 w-3" />;
    } else if (ev.eventType === 'Orientation') {
    typeColor = 'bg-emerald-50 border-emerald-100 text-emerald-700';
    iconElement = <Award className="h-3 w-3" />;
    } else if (ev.eventType === 'Training') {
    typeColor = 'bg-amber-50 border-amber-100 text-amber-700';
    iconElement = <Briefcase className="h-3 w-3" />;
    } else if (ev.eventType === 'Flight') {
    typeColor = 'bg-blue-50 border-blue-110 text-brand';
    iconElement = <Compass className="h-3 w-3" />;
    } else if (ev.eventType === 'Medical') {
    typeColor = 'bg-rose-50 border-rose-100 text-rose-700';
    iconElement = <AlertCircle className="h-3 w-3" />;
    }

    let statusColor = 'bg-amber-100 text-amber-805';
    if (ev.status === 'Completed') statusColor = 'bg-emerald-100 text-emerald-800';
    else if (ev.status === 'Cancelled' || ev.status === 'Declined') statusColor = 'bg-rose-100 text-rose-800';
    else if (ev.status === 'Confirmed') statusColor = 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    else if (ev.status === 'Rescheduled') statusColor = 'bg-indigo-100 text-indigo-800';
    else if (ev.status === 'Pending' || ev.status === 'Awaiting Response') statusColor = 'bg-blue-100 text-blue-800';

    return (
    <tr key={ev.eventId} onClick={() => { setSlideInEvent(ev); setDashboardTab('overview'); }}
    className="hover:bg-slate-50/50 transition-colors cursor-pointer">
    <td className="py-3.5 px-5 whitespace-nowrap">
    <div className="flex items-center space-x-2 font-mono">
    <Clock className="h-3.5 w-3.5 text-slate-400" />
    <span className="font-bold text-slate-700">
    {new Date(ev.dateTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
    </span>
    <span className="text-slate-400 font-semibold">
    {ev.isGroupEvent ? ev.groupEventConfig?.blockedTimes?.[0]?.startTime || '09:00' : new Date(ev.dateTime).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
    </span>
    {ev.timezone && (
    <span className="text-caption font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
    {ev.timezone.split('/').pop()?.replace('_', ' ')}
    </span>
    )}
    </div>
    </td>
    <td className="py-3.5 px-5">
    <div className="space-y-1">
    <div className="flex items-center space-x-2">
    <span className="font-extrabold text-slate-800 tracking-tight font-display text-[13px]">
    {ev.title}
    </span>
    <span className={`inline-flex items-center space-x-1 px-2 py-0.5 border rounded-lg text-caption font-extrabold uppercase ${typeColor}`}>
    {iconElement}
    <span>{ev.eventType}</span>
    </span>
    {ev.isGroupEvent && (
      <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[11px] font-black uppercase px-2 py-0.5 rounded-md">
        Group Event
      </span>
    )}
    </div>
    {ev.description && (
    <p className="text-slate-600 text-caption font-medium leading-normal max-w-sm whitespace-normal break-words">
    {ev.description}
    </p>
    )}
    </div>
    </td>
    <td className="py-3.5 px-5">
    {ev.isGroupEvent ? (() => {
      const slots = ev.slots || [];
      const total = slots.length;
      const booked = slots.filter((s: any) => s.bookingStatus === 'booked').length;
      const pct = total > 0 ? (booked / total) * 100 : 0;
      return (
        <div className="space-y-1 max-w-[140px]">
          <div className="flex justify-between items-center text-[10px] font-bold text-slate-600">
            <span>Roster Openings</span>
            <span>{booked} / {total}</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
            <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    })() : assignedApp ? (
    <div className="flex items-center space-x-2">
    <div className="h-7 w-7 rounded-full bg-blue-50 border border-slate-200 overflow-hidden flex items-center justify-center text-caption font-bold text-brand">
    {assignedApp.profilePhotoUrl ? (
    <img src={assignedApp.profilePhotoUrl} alt="avatar" className="h-full w-full object-cover" />
    ) : (
    getSafeApplicantName(assignedApp).charAt(0) || 'U'
    )}
    </div>
    <div className="leading-tight">
    <p className="font-bold text-slate-900">
    {getSafeApplicantName(assignedApp)}
    </p>
    <p className="text-slate-400 text-caption font-semibold">{assignedApp.userEmail}</p>
    <a href={`/applications/adminview/${assignedApp.applicationId}`}
       target="_blank"
       rel="noopener noreferrer"
       className="text-[10px] text-blue-600 hover:underline font-extrabold block mt-0.5"
       onClick={(e) => e.stopPropagation()}
    >
      URL: /applications/adminview/{assignedApp.applicationId}
    </a>
    </div>
    </div>
    ) : (
    <span className="text-slate-500 italic">No candidate assigned</span>
    )}
    </td>
    <td className="py-3.5 px-5 whitespace-nowrap">
      {(() => {
        const teamNames = Array.isArray(ev.assignedTeam) ? ev.assignedTeam.map((m: any) => m.name || m.userName).filter(Boolean) : [];
        const hostName = ev.assignedToName || (ev.assignedTo ? agentsList.find(a => a.uid === ev.assignedTo)?.name : null) || teamNames[0] || (ev.assignedTo && !ev.assignedTo.startsWith('uid') ? ev.assignedTo : null);

        return (
          <div className="flex items-center space-x-1.5 font-medium text-slate-700">
            <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span className="font-bold text-slate-800 text-xs">
              {hostName || 'Unassigned'}
            </span>
          </div>
        );
      })()}
    </td>
    <td className="py-3.5 px-5 font-semibold text-slate-600">
    <div className="flex items-center space-x-1">
    <MapPin className="h-3 w-3 text-slate-400" />
    <span className="max-w-[140px] truncate">{ev.location}</span>
    </div>
    {!ev.isGroupEvent && ev.isVirtual && ev.meetingUrl && ev.status !== 'Cancelled' && (
      <a href={ev.meetingUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
        className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white text-[10px] font-bold transition-all cursor-pointer">
        <Video className="h-3 w-3" /> Join Meeting
      </a>
    )}
    </td>
    <td className="py-3.5 px-5">
    <span className={`text-caption font-extrabold px-2 py-0.5 rounded-full tracking-wider ${statusColor}`}>
    {ev.status}
    </span>
    </td>
    <td className="py-3.5 px-5 text-right">
    <div className="inline-flex space-x-2 justify-end">
    {ev.status === 'Pending' && (
      <>
      <button
      onClick={(e) => { e.stopPropagation(); handleProposalDecision(ev, 'approve'); }}
      className="p-1 px-2 border border-emerald-200 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-all flex items-center space-x-1 font-bold cursor-pointer"
      title="Approve requested new time"
      >
      <Check className="h-3 w-3" />
      <span>Approve</span>
      </button>
      <button
      onClick={(e) => { e.stopPropagation(); handleProposalDecision(ev, 'reject'); }}
      className="p-1 px-2 border border-rose-200 hover:bg-rose-50 text-rose-600 rounded-lg transition-all flex items-center space-x-1 font-bold cursor-pointer"
      title="Reject requested new time"
      >
      <X className="h-3 w-3" />
      <span>Reject</span>
      </button>
      </>
    )}
    <button
    onClick={(e) => { e.stopPropagation(); handleEditEventClickLocal(ev); }}
    className="p-1 px-2 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-600 rounded-lg transition-all flex items-center space-x-1 font-bold cursor-pointer"
    title="Edit Event"
    >
    <Edit3 className="h-3 w-3 text-slate-500" />
    <span>Edit</span>
    </button>
    <button
    onClick={(e) => { e.stopPropagation(); handleDeleteEvent(ev.eventId, ev.title); }}
    className="p-1 px-2 border border-rose-200 hover:bg-rose-50 hover:border-rose-350 text-rose-600 rounded-lg transition-all flex items-center space-x-1 font-bold cursor-pointer"
    title="Delete Event"
    >
    <Trash2 className="h-3 w-3 text-rose-505" />
    <span>Delete</span>
    </button>
    </div>
    </td>
    </tr>
    );
    })}
    </tbody>
    </table>
    </div>
    </div>
    )}

    {/* Events Pagination Controls */}
    {eventViewMode === 'list' && filteredEvents.length > eventsPageSize && (
    <div className="flex items-center justify-between pt-2 pb-1">
    <span className="text-caption text-slate-400 font-medium">
    Page {eventsPage} of {eventsTotalPages} ({filteredEvents.length} total events)
    </span>
    <div className="flex items-center space-x-1.5">
    <button
    onClick={() => setEventsPage(Math.max(1, eventsPage - 1))}
    disabled={eventsPage <= 1}
    className="px-3 py-1.5 text-caption font-bold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer flex items-center space-x-1"
    >
    <ChevronLeft className="h-3 w-3" />
    <span>Prev</span>
    </button>
    {Array.from({ length: Math.min(eventsTotalPages, 7) }, (_, i) => {
    let pageNum: number;
    if (eventsTotalPages <= 7) {
    pageNum = i + 1;
    } else if (eventsPage <= 4) {
    pageNum = i + 1;
    } else if (eventsPage >= eventsTotalPages - 3) {
    pageNum = eventsTotalPages - 6 + i;
    } else {
    pageNum = eventsPage - 3 + i;
    }
    return (
    <button
    key={pageNum}
    onClick={() => setEventsPage(pageNum)}
    className={`px-2.5 py-1.5 text-caption font-bold rounded-lg transition-all cursor-pointer ${
    eventsPage === pageNum
    ? 'btn-primary text-white shadow-sm'
    : 'bg-white border border-slate-200 hover:bg-slate-50 text-slate-600'
    }`}
    >
    {pageNum}
    </button>
    );
    })}
    <button
    onClick={() => setEventsPage(Math.min(eventsTotalPages, eventsPage + 1))}
    disabled={eventsPage >= eventsTotalPages}
    className="px-3 py-1.5 text-caption font-bold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer flex items-center space-x-1"
    >
    <span>Next</span>
    <ChevronRight className="h-3 w-3" />
    </button>
    </div>
    </div>
    )}

    {/* ─── EVENT DETAIL MODAL (popup matching Create Event style) ───── */}
    {(focusedEvent || slideInEvent) && (() => {
    const ev = focusedEvent || slideInEvent!;
    const isGroup = !!ev.isGroupEvent;

    return (
    <Modal onClose={() => { setFocusedEvent(null); setSlideInEvent(null); }} title="Event details">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>

    {/* Header — matches Create Event gradient header */}
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 shrink-0">
    <div className="flex items-center justify-between">
    <div className="flex items-center space-x-3">
    <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
    <Calendar className="h-4 w-4 text-white" />
    </div>
    <div>
    <h3 className="text-sm font-bold text-white font-display">
      {isGroup ? "Group Event Dashboard" : "Event Details"}
    </h3>
    <p className="text-caption text-blue-100 mt-0.5">
      {isGroup ? "Real-time Roster, Slots Booking, & Communication" : "View and manage scheduled events"}
    </p>
    </div>
    </div>
    <button
    onClick={() => { setFocusedEvent(null); setSlideInEvent(null); }}
    className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition-all cursor-pointer"
    >
    <X className="h-5 w-5" />
    </button>
    </div>
    </div>

    {/* Body */}
    {isGroup ? (
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-5 py-2 text-xs shrink-0 font-bold">
          <button
            onClick={() => setDashboardTab('overview')}
            className={`px-4 py-2 rounded-lg transition-all ${dashboardTab === 'overview' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setDashboardTab('slots')}
            className={`px-4 py-2 rounded-lg transition-all ${dashboardTab === 'slots' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Slots & Bookings ({(ev.slots || []).length})
          </button>
          <button
            onClick={() => {
              setDashboardTab('broadcast');
              // Pre-select all booked attendee emails
              const bookedEmails = (ev.slots || [])
                .filter((s: any) => s.bookingStatus === 'booked' && s.bookedBy?.email)
                .map((s: any) => s.bookedBy.email);
              setSelectedAttendeeEmails(bookedEmails);
            }}
            className={`px-4 py-2 rounded-lg transition-all ${dashboardTab === 'broadcast' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Broadcast Email
          </button>
          <button
            onClick={() => setDashboardTab('invitations')}
            className={`px-4 py-2 rounded-lg transition-all ${dashboardTab === 'invitations' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Invitations ({Object.keys(ev.invitations || {}).length})
          </button>
          <button
            onClick={() => setDashboardTab('notes')}
            className={`px-4 py-2 rounded-lg transition-all ${dashboardTab === 'notes' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Notes
          </button>
          <button
            onClick={() => setDashboardTab('results')}
            className={`px-4 py-2 rounded-lg transition-all ${dashboardTab === 'results' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Results
          </button>
        </div>

        {/* Tab Content Panel */}
        <div className="p-5 flex-1 overflow-y-auto space-y-5 text-xs text-slate-700">
          {dashboardTab === 'overview' && (
            <div className="space-y-4">
              <div className="flex justify-between items-start flex-wrap gap-4">
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">{ev.title}</h3>
                  <p className="text-slate-500 font-semibold mt-0.5">Category: {ev.eventType} · Status: {ev.status}</p>
                </div>
                <button
                  onClick={() => handleExportRoster(ev)}
                  className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg font-bold flex items-center gap-1.5 shadow-sm text-[11px]"
                >
                  <Download className="h-3.5 w-3.5 text-slate-500" />
                  <span>Export Roster CSV</span>
                </button>
              </div>

              {/* Stats occupancy meter */}
              {(() => {
                const total = (ev.slots || []).length;
                const booked = (ev.slots || []).filter((s: any) => s.bookingStatus === 'booked').length;
                const remaining = total - booked;
                const pct = total > 0 ? (booked / total) * 100 : 0;
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
                    <div className="text-center sm:text-left">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Roster Occupancy</p>
                      <p className="text-lg font-black text-slate-900 mt-1">{pct.toFixed(0)}%</p>
                      <div className="w-full bg-slate-200 rounded-full h-2 mt-2 overflow-hidden">
                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-semibold">Total Slots</p>
                      <p className="text-lg font-black text-blue-600 mt-1">{total}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-semibold">Booked Slots</p>
                      <p className="text-lg font-black text-emerald-600 mt-1">{booked}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 font-semibold">Remaining Slots</p>
                      <p className="text-lg font-black text-slate-600 mt-1">{remaining}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Public Booking Link Clipboard Copy */}
              <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-4 space-y-2">
                <h4 className="text-[10px] uppercase font-bold text-blue-600 tracking-wider flex items-center gap-1.5">
                  <Link className="h-3.5 w-3.5" />
                  Public Booking Link
                </h4>
                <p className="text-[11px] text-slate-500 font-medium">Share this secure unique URL via Email, SMS, or other channels to allow candidates to pick their slots:</p>
                <div className="flex gap-2 items-center bg-white p-2 border border-slate-200 rounded-lg">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/group-schedule/${ev.eventId}`}
                    className="flex-1 bg-transparent border-none outline-none font-mono text-[11px] font-semibold text-slate-700 select-all"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/group-schedule/${ev.eventId}`);
                      notifyToast('success', 'Public booking link copied to clipboard!');
                    }}
                    className="px-3 py-1 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700 transition-all text-[11px]"
                  >
                    Copy Link
                  </button>
                </div>
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Date & Time boundaries</span>
                  <p className="font-bold text-slate-800">
                    {new Date(ev.dateTime).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  <p className="font-semibold text-slate-500">Timezone: {ev.timezone}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Location & Virtual Meeting</span>
                  <p className="font-bold text-slate-800">
                    {ev.groupEventConfig?.isVirtual ? 'Virtual Meeting Room (Unique Jitsi URLs generated per slot)' : ev.location || '—'}
                  </p>
                </div>
              </div>

              {ev.description && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-1">
                  <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block mb-1">Preparation Instructions</span>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{ev.description}</p>
                </div>
              )}
            </div>
          )}

          {dashboardTab === 'slots' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-slate-800 text-xs">Appointment Slots Schedule</h3>
                <span className="text-[11px] text-slate-500 font-medium">Use checkboxes to filter recipients for Email Broadcasting.</span>
              </div>

              {/* Interactive manual assignment panel inline/modal */}
              {assigningSlotId && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 animate-fade-in">
                  <div className="flex justify-between items-center">
                    <h4 className="font-bold text-slate-800">Manually Assign Applicant to Slot</h4>
                    <button onClick={() => setAssigningSlotId(null)} className="text-slate-400 hover:text-slate-600">
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Applicant Search */}
                  <div className="space-y-1 relative">
                    <label className="text-[10px] font-bold uppercase text-slate-400">Search Workspace Candidates</label>
                    <input
                      type="text"
                      placeholder="Type name or email to filter applicants..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-white border border-slate-200 rounded-lg p-2 font-semibold"
                    />

                    {/* Search results dropdown */}
                    {searchQuery.trim() && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto z-50 divide-y divide-slate-100">
                        {applications
                          .filter(a => {
                            const name = getSafeApplicantName(a).toLowerCase();
                            const email = (a.userEmail || '').toLowerCase();
                            return name.includes(searchQuery.toLowerCase()) || email.includes(searchQuery.toLowerCase());
                          })
                          .slice(0, 10)
                          .map(a => (
                            <button
                              key={a.applicationId}
                              type="button"
                              onClick={() => {
                                setAssignName(getSafeApplicantName(a));
                                setAssignEmail(a.userEmail || '');
                                setAssignAppId(a.applicationId);
                                setSearchQuery(''); // close dropdown
                              }}
                              className="w-full text-left px-3 py-2 text-[11px] hover:bg-slate-50 flex flex-col font-medium"
                            >
                              <span className="font-bold text-slate-900">{getSafeApplicantName(a)}</span>
                              <span className="text-slate-500">{a.userEmail} · {a.programType}</span>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>

                  {/* Manual forms if they want to override */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase text-slate-400">Name</label>
                      <input
                        type="text"
                        value={assignName}
                        onChange={(e) => setAssignName(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 font-semibold"
                        placeholder="Jean-Pierre"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase text-slate-400">Email</label>
                      <input
                        type="email"
                        value={assignEmail}
                        onChange={(e) => setAssignEmail(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg p-2 font-semibold"
                        placeholder="jean.pierre@example.com"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => setAssigningSlotId(null)}
                      className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg font-bold"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleAssignSlot(assigningSlotId)}
                      disabled={assigning}
                      className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold disabled:opacity-50"
                    >
                      {assigning ? 'Assigning...' : 'Confirm Assignment'}
                    </button>
                  </div>
                </div>
              )}

              {/* Rescheduling available slots sub-list */}
              {reschedulingSlotId && (() => {
                const oldSlot = (ev.slots || []).find((s: any) => s.id === reschedulingSlotId);
                const unbookedSlots = (ev.slots || []).filter((s: any) => s.bookingStatus !== 'booked');

                return (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 animate-fade-in">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-bold text-slate-800">Reschedule Booking</h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">Moving <strong>{oldSlot?.bookedBy?.name}</strong> from <strong>{oldSlot?.startTime} - {oldSlot?.endTime}</strong></p>
                      </div>
                      <button onClick={() => setReschedulingSlotId(null)} className="text-slate-400 hover:text-slate-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {unbookedSlots.length === 0 ? (
                      <p className="text-xs text-slate-500 font-semibold italic py-2 text-center">No other available slots to reschedule to.</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 pt-1 max-h-36 overflow-y-auto">
                        {unbookedSlots.map(s => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => handleRescheduleBooking(reschedulingSlotId, s.id, oldSlot.bookedBy)}
                            className="p-2 border border-slate-200 bg-white hover:border-blue-500 hover:bg-blue-50 rounded-lg font-bold font-mono text-center text-slate-700 transition-all cursor-pointer"
                          >
                            {s.startTime}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Bulk Approval Management Toolbar */}
              {(() => {
                const pendingSlots = (ev.slots || []).filter((s: any) => s.bookingStatus === 'pending');
                if (pendingSlots.length === 0) return null;
                return (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-center justify-between text-xs text-amber-900 animate-fade-in">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                      <div>
                        <span className="font-bold">Pending Booking Requests ({pendingSlots.length}):</span>
                        <span className="text-[11px] text-amber-700 ml-1">Review applicant requests requiring manual administrator approval.</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleApproveSlot(selectedPendingSlotIds.length > 0 ? selectedPendingSlotIds : pendingSlots.map((s: any) => s.id), 'approve')}
                        disabled={approvingSlot}
                        className="px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 text-white font-bold rounded-lg transition-all shadow-sm cursor-pointer"
                      >
                        Approve {selectedPendingSlotIds.length > 0 ? `(${selectedPendingSlotIds.length})` : 'All Pending'}
                      </button>
                      <button
                        onClick={() => handleApproveSlot(selectedPendingSlotIds.length > 0 ? selectedPendingSlotIds : pendingSlots.map((s: any) => s.id), 'decline')}
                        disabled={approvingSlot}
                        className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold rounded-lg transition-all shadow-sm cursor-pointer"
                      >
                        Decline {selectedPendingSlotIds.length > 0 ? `(${selectedPendingSlotIds.length})` : 'All Pending'}
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Slots Table */}
              <div className="border border-slate-200 rounded-xl overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[650px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-extrabold uppercase text-[10px] tracking-wider">
                      <th className="py-2 px-3 w-8">
                        <input
                          type="checkbox"
                          checked={selectedPendingSlotIds.length > 0 && selectedPendingSlotIds.length === (ev.slots || []).filter((s: any) => s.bookingStatus === 'pending').length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const pendingIds = (ev.slots || [])
                                .filter((s: any) => s.bookingStatus === 'pending')
                                .map((s: any) => s.id);
                              setSelectedPendingSlotIds(pendingIds);
                            } else {
                              setSelectedPendingSlotIds([]);
                            }
                          }}
                          className="rounded border-slate-300 text-brand"
                        />
                      </th>
                      <th className="py-2 px-3">Slot Interval</th>
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 px-3">Applicant Name</th>
                      <th className="py-2 px-3">Email</th>
                      <th className="py-2 px-3">Meeting Room</th>
                      <th className="py-2 px-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-[11px]">
                    {(ev.slots || []).map((slot: any) => {
                      const isBooked = slot.bookingStatus === 'booked';
                      const isPending = slot.bookingStatus === 'pending';
                      const hasAttendee = isBooked || isPending;
                      const bBy = slot.bookedBy || {};
                      const isPendingSelected = isPending && selectedPendingSlotIds.includes(slot.id);

                      return (
                        <tr key={slot.id} className="hover:bg-slate-50/50">
                          <td className="py-2 px-3">
                            {isPending ? (
                              <input
                                type="checkbox"
                                checked={isPendingSelected}
                                onChange={() => {
                                  if (isPendingSelected) {
                                    setSelectedPendingSlotIds(selectedPendingSlotIds.filter(x => x !== slot.id));
                                  } else {
                                    setSelectedPendingSlotIds([...selectedPendingSlotIds, slot.id]);
                                  }
                                }}
                                className="rounded border-slate-300 text-brand"
                              />
                            ) : (
                              <div className="w-4 h-4 bg-slate-50 border border-slate-200 rounded" />
                            )}
                          </td>
                          <td className="py-2 px-3 font-mono font-bold text-slate-700 whitespace-nowrap">
                            {slot.startTime} - {slot.endTime}
                          </td>
                          <td className="py-2 px-3">
                            {isBooked ? (
                              <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-bold text-[11px] uppercase">
                                Confirmed
                              </span>
                            ) : isPending ? (
                              <span className="bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full font-bold text-[11px] uppercase">
                                Pending Approval
                              </span>
                            ) : (
                              <span className="bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 rounded-full font-bold text-[11px] uppercase">
                                Available
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            {hasAttendee ? (
                              <div className="font-bold text-slate-800">{sanitizeNamePart(bBy.name) || 'Applicant'}</div>
                            ) : (
                              <span className="text-slate-500 italic">Available</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-slate-500 font-semibold truncate max-w-[150px]">
                            {hasAttendee ? bBy.email : '—'}
                            {hasAttendee && bBy.applicationId && (
                              <a href={`/applications/adminview/${bBy.applicationId}`}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 className="text-[11px] text-blue-600 hover:underline font-extrabold block mt-0.5"
                                 onClick={(e) => e.stopPropagation()}
                              >
                                URL: /applications/adminview/{bBy.applicationId}
                              </a>
                            )}
                          </td>
                          <td className="py-2 px-3">
                            {hasAttendee && slot.meetingUrl ? (
                              <a
                                href={slot.meetingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 rounded-md font-bold text-[10px] transition-all"
                              >
                                <Video className="h-3 w-3 shrink-0" />
                                <span>
                                  {slot.meetingUrl.includes('daily.co')
                                    ? 'Join Daily'
                                    : slot.meetingUrl.includes('meet.jit.si')
                                      ? 'Join Jitsi'
                                      : slot.meetingUrl.includes('zoom.us')
                                        ? 'Join Zoom'
                                        : slot.meetingUrl.includes('meet.google.com')
                                          ? 'Join Google Meet'
                                          : 'Join Meeting'}
                                </span>
                              </a>
                            ) : '—'}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {isPending ? (
                              <div className="inline-flex gap-1.5">
                                <button
                                  onClick={() => handleApproveSlot([slot.id], 'approve')}
                                  disabled={approvingSlot}
                                  className="px-2.5 py-1 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-md text-[10px] font-bold transition-all cursor-pointer"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleApproveSlot([slot.id], 'decline')}
                                  disabled={approvingSlot}
                                  className="px-2 py-1 border border-rose-200 bg-white hover:bg-rose-50 text-rose-600 rounded-md text-[10px] font-bold transition-all cursor-pointer"
                                >
                                  Decline
                                </button>
                              </div>
                            ) : isBooked ? (
                              <div className="inline-flex gap-1.5">
                                <button
                                  onClick={() => setReschedulingSlotId(slot.id)}
                                  className="px-2 py-1 border border-slate-200 bg-white hover:bg-slate-50 rounded-md text-[10px] font-bold text-slate-600 transition-all cursor-pointer"
                                >
                                  Reschedule
                                </button>
                                <button
                                  onClick={() => handleCancelSlotBooking(slot.id)}
                                  className="px-2 py-1 border border-rose-200 bg-white hover:bg-rose-50 rounded-md text-[10px] font-bold text-rose-600 transition-all cursor-pointer"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setAssigningSlotId(slot.id);
                                  setAssignName('');
                                  setAssignEmail('');
                                  setAssignAppId('');
                                  setSearchQuery('');
                                }}
                                className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-[10px] font-bold transition-all cursor-pointer"
                              >
                                Assign
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

            </div>
          )}

          {dashboardTab === 'broadcast' && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <h3 className="font-bold text-slate-900">Send Email Broadcast to Attendees</h3>
                <p className="text-xs text-slate-500 mt-0.5">Target {selectedAttendeeEmails.length} recipient(s) selected from your roster slots list.</p>
              </div>

              <div className="space-y-3 text-xs">
                {/* Subject */}
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Broadcast Subject</label>
                  <input
                    type="text"
                    placeholder="e.g. Schedule Update or Preparation materials"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 font-semibold text-slate-800"
                    value={broadcastSubject}
                    onChange={(e) => setBroadcastSubject(e.target.value)}
                  />
                </div>

                {/* Message Body */}
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Message Body (Markdown Supported)</label>
                  <textarea
                    rows={6}
                    placeholder="Type your announcement details here. Markdown headers, lists, links, and bold text will render correctly in their email client."
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2 text-slate-800 font-medium"
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                  />
                </div>

                <div className="flex justify-end pt-1">
                  <button
                    onClick={handleSendBroadcast}
                    disabled={broadcasting}
                    className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl font-bold flex items-center gap-1.5 transition-all shadow-md shadow-blue-500/10 cursor-pointer"
                  >
                    {broadcasting ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Sending Broadcast...</span>
                      </>
                    ) : (
                      <>
                        <Send className="h-3.5 w-3.5" />
                        <span>Send Broadcast Email</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {dashboardTab === 'invitations' && (() => {
            const invitationsMap = ev.invitations || {};
            const invitedAppIds = Object.keys(invitationsMap);

            // Filter available applicants to invite
            const filteredApplicants = applications.filter(app => {
              // Exclude already invited
              if (invitedAppIds.includes(app.applicationId)) return false;

              // Search Query
              if (inviteSearch.trim()) {
                const q = inviteSearch.toLowerCase();
                const fullName = getSafeApplicantName(app).toLowerCase();
                const email = (app.userEmail || '').toLowerCase();
                const idStr = String(app.applicationId || '').toLowerCase();
                if (!fullName.includes(q) && !email.includes(q) && !idStr.includes(q)) return false;
              }

              // Program filter
              if (inviteProgramFilter !== 'all') {
                if (app.programId !== inviteProgramFilter) return false;
              }

              // Term filter
              if (inviteTermFilter !== 'all') {
                if (app.programTerm !== inviteTermFilter) return false;
              }

              // Stage filter
              if (inviteStageFilter !== 'all') {
                if (String(app.masterStage) !== inviteStageFilter) return false;
              }

              // Status filter
              if (inviteStatusFilter !== 'all') {
                if (app.submissionStatus !== inviteStatusFilter) return false;
              }

              return true;
            });

            // Handlers
            const inviteApplicant = async (appId: string) => {
              setIsSubmittingInvite(true);
              try {
                await safeFetch(`/api/admin/group-events/${ev.eventId}/invitations`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ applicationIds: [appId] })
                });
                notifyToast('success', 'Group Event invitation task queued! Tracking progress in Task Tracker.');
                window.dispatchEvent(new CustomEvent('bulk-job-created'));
                fetchEvents();
              } catch (err: any) {
                notifyToast('error', err.message || 'Failed to send invitation');
              } finally {
                setIsSubmittingInvite(false);
              }
            };

            const removeInvitation = async (appId: string) => {
              if (!(await confirmDialog('Are you sure you want to remove this applicant? This will also unbook any slots they have reserved (no email notice will be sent).'))) return;
              setIsSubmittingInvite(true);
              try {
                await safeFetch(`/api/admin/group-events/${ev.eventId}/invitations/remove`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ applicationId: appId })
                });
                setSelectedInvitedAppIds(prev => prev.filter(id => id !== appId));
                fetchEvents();
              } catch (err: any) {
                notifyToast('error', err.message || 'Failed to remove invitation');
              } finally {
                setIsSubmittingInvite(false);
              }
            };

            const removeBulkInvitations = async (appIds: string[]) => {
              if (appIds.length === 0) return;
              if (!(await confirmDialog(`Are you sure you want to remove ${appIds.length} applicant invitation(s)? This will also unbook any reserved slots (no email notice will be sent).`))) return;
              setIsSubmittingInvite(true);
              try {
                await safeFetch(`/api/admin/group-events/${ev.eventId}/invitations/remove`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ applicationIds: appIds })
                });
                setSelectedInvitedAppIds([]);
                fetchEvents();
              } catch (err: any) {
                notifyToast('error', err.message || 'Failed to remove invitations');
              } finally {
                setIsSubmittingInvite(false);
              }
            };

            const resendInvitation = async (appId: string, recipient?: string) => {
              if (!(await confirmDialog(`Resend the invitation email with the booking link for "${ev.title}" to ${recipient || 'this applicant'}?`, { confirmLabel: 'Resend invitation' }))) return;
              setIsSubmittingInvite(true);
              try {
                await safeFetch(`/api/admin/group-events/${ev.eventId}/invitations/resend`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ applicationId: appId })
                });
                notifyToast('success', 'Invitation email resent successfully with booking link!');
                fetchEvents();
                if (slideInEvent && slideInEvent.eventId === ev.eventId) {
                  const updatedDoc = await getDocs(query(collection(db, 'events')));
                  updatedDoc.forEach(d => {
                    if (d.id === ev.eventId) {
                      setSlideInEvent({ eventId: d.id, ...d.data() } as Event);
                    }
                  });
                }
              } catch (err: any) {
                notifyToast('error', err.message);
              } finally {
                setIsSubmittingInvite(false);
              }
            };

            const handleSendMassReminder = async () => {
              const bookedAppIds = new Set(
                (ev.slots || [])
                  .filter((s: any) => s.bookingStatus === 'booked' && s.bookedBy?.applicationId)
                  .map((s: any) => s.bookedBy.applicationId)
              );
              const unbookedCount = invitedAppIds.filter(appId => !bookedAppIds.has(appId)).length;

              if (unbookedCount === 0) {
                notifyToast('warning', 'All invited applicants have already booked their slots.');
                return;
              }

              const confirmRemind = await confirmDialog(
                `Queue Mass Reminder?\n\nThis queues a background reminder job for all invited applicants who have not yet booked a time. The reminders are sent by the job, not while you wait.\n\n${unbookedCount} applicants will receive a reminder.\n\nDo you want to proceed?`
              );
              if (!confirmRemind) return;

              setIsSubmittingInvite(true);
              try {
                const remindData: any = await safeFetch(`/api/admin/group-events/${ev.eventId}/remind-all`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' }
                });
                notifyToast('success', `Mass reminder queued for ${remindData?.count ?? unbookedCount} unbooked invitee(s). The background job sends the reminders; track progress in Task Tracker.`);
                window.dispatchEvent(new CustomEvent('bulk-job-created'));
                fetchEvents();
              } catch (err: any) {
                notifyToast('error', err.message);
              } finally {
                setIsSubmittingInvite(false);
              }
            };

            const updateInvitationStatus = async (appId: string, status: string) => {
              try {
                await safeFetch(`/api/admin/group-events/${ev.eventId}/invitations/update-status`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ applicationId: appId, status })
                });
                fetchEvents();
                if (slideInEvent && slideInEvent.eventId === ev.eventId) {
                  const updatedDoc = await getDocs(query(collection(db, 'events')));
                  updatedDoc.forEach(d => {
                    if (d.id === ev.eventId) {
                      setSlideInEvent({ eventId: d.id, ...d.data() } as Event);
                    }
                  });
                }
              } catch (err: any) {
                notifyToast('error', err.message);
              }
            };

            return (
              <div className="space-y-6 animate-fade-in text-xs text-slate-700">
                {/* Currently Invited Candidates Section */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                  <h4 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider flex items-center justify-between">
                    <span>Currently Invited ({invitedAppIds.length})</span>
                    <div className="flex items-center gap-2">
                      {selectedInvitedAppIds.length > 0 && (
                        <button
                          type="button"
                          onClick={() => removeBulkInvitations(selectedInvitedAppIds)}
                          disabled={isSubmittingInvite}
                          className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-bold flex items-center gap-1.5 shadow-sm text-[10px] cursor-pointer transition-all disabled:opacity-50"
                        >
                          <Trash2 className="h-3 w-3" />
                          <span>Remove Selected ({selectedInvitedAppIds.length})</span>
                        </button>
                      )}
                      {(() => {
                        const bookedAppIds = new Set(
                          (ev.slots || [])
                            .filter((s: any) => s.bookingStatus === 'booked' && s.bookedBy?.applicationId)
                            .map((s: any) => s.bookedBy.applicationId)
                        );
                        const unbookedCount = invitedAppIds.filter(appId => !bookedAppIds.has(appId)).length;
                        if (unbookedCount > 0) {
                          return (
                            <button
                              type="button"
                              onClick={handleSendMassReminder}
                              disabled={isSubmittingInvite}
                              className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold flex items-center gap-1.5 shadow-sm text-[10px] cursor-pointer transition-all disabled:opacity-50"
                            >
                              <Send className="h-3 w-3" />
                              <span>Send Mass Reminder ({unbookedCount})</span>
                            </button>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </h4>
                  {invitedAppIds.length === 0 ? (
                    <p className="text-slate-400 text-center py-4">
                      No candidates have been invited yet. Select candidates from the list and use the invite action to add them — invited candidates will appear here with their booking status.
                    </p>
                  ) : (
                    <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                      <table className="w-full text-left border-collapse min-w-[600px]">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200 font-bold text-slate-500 text-[10px] uppercase">
                            <th className="p-2.5 w-8 text-center">
                              <input
                                type="checkbox"
                                checked={invitedAppIds.length > 0 && selectedInvitedAppIds.length === invitedAppIds.length}
                                onChange={(e) => setSelectedInvitedAppIds(e.target.checked ? [...invitedAppIds] : [])}
                                className="rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                              />
                            </th>
                            <th className="p-2.5">Name / Email</th>
                            <th className="p-2.5">Application Info</th>
                            <th className="p-2.5">Invite Date</th>
                            <th className="p-2.5">Status</th>
                            <th className="p-2.5 text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {invitedAppIds.map(appId => {
                            const invite = invitationsMap[appId];
                            const appObj = applications.find(a => a.applicationId === appId);
                            const name = sanitizeNamePart(invite.userName) || getSafeApplicantName(appObj);
                            const isBooked = (ev.slots || []).some((s: any) => s.bookingStatus === 'booked' && s.bookedBy?.applicationId === appId);
                            const isChecked = selectedInvitedAppIds.includes(appId);
                            return (
                              <tr key={appId} className="hover:bg-slate-50/50">
                                <td className="p-2.5 text-center">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => setSelectedInvitedAppIds(prev => e.target.checked ? [...prev, appId] : prev.filter(id => id !== appId))}
                                    className="rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                                  />
                                </td>
                                <td className="p-2.5">
                                  <div className="font-bold text-slate-800">{name}</div>
                                  <div className="text-[11px] text-slate-500 font-mono">{invite.userEmail}</div>
                                </td>
                                <td className="p-2.5">
                                  <div className="text-slate-600 font-medium">ID: {appId}</div>
                                  <div className="text-[11px] text-slate-500">{appObj?.programType} · {appObj?.programTerm || 'No term'}</div>
                                  <a href={`/applications/adminview/${appId}`}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     className="text-[10px] text-blue-600 hover:underline font-extrabold block mt-0.5"
                                     onClick={(e) => e.stopPropagation()}
                                  >
                                    URL: /applications/adminview/{appId}
                                  </a>
                                </td>
                                <td className="p-2.5 font-medium text-slate-500">
                                  {invite.invitedAt ? new Date(invite.invitedAt).toLocaleDateString() : '—'}
                                  <div className="text-[11px] text-slate-500">By {invite.invitedByName || 'Admin'}</div>
                                </td>
                                <td className="p-2.5">
                                  {isBooked ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                                      Booked
                                    </span>
                                  ) : (
                                    <select
                                      value={invite.status || 'Invited'}
                                      onChange={(e) => updateInvitationStatus(appId, e.target.value)}
                                      className="border border-slate-200 bg-white rounded p-1 font-semibold text-slate-800 text-[11px]"
                                    >
                                      <option value="Invited">Invited</option>
                                      <option value="Registered">Registered</option>
                                      <option value="Declined">Declined</option>
                                      <option value="Cancelled">Cancelled</option>
                                      <option value="Attended">Attended</option>
                                      <option value="No Show">No Show</option>
                                    </select>
                                  )}
                                </td>
                                <td className="p-2.5 text-center flex items-center justify-center space-x-1.5">
                                  {!isBooked && (
                                    <button
                                      onClick={() => resendInvitation(appId, `${name} (${(invite as any).userEmail || ''})`)}
                                      disabled={isSubmittingInvite}
                                      className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-all cursor-pointer inline-flex items-center justify-center"
                                      title="Resend Invitation Email & Link"
                                    >
                                      <Send className="h-4 w-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => removeInvitation(appId)}
                                    disabled={isSubmittingInvite}
                                    className="p-1 text-rose-500 hover:bg-rose-50 rounded transition-all cursor-pointer inline-flex items-center justify-center"
                                    title="Remove Invitation"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Add New Applicants Panel */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
                  <div>
                    <h4 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider">Search & Add Workspace Candidates</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Filter the master candidate catalogue to invite applicants meeting the criteria.</p>
                  </div>

                  {/* Filters grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500">Search</label>
                      <input
                        type="text"
                        placeholder="Search name, email, ID..."
                        value={inviteSearch}
                        onChange={e => setInviteSearch(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg p-1.5 font-medium"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500">Program</label>
                      <SearchableSelect
                        value={inviteProgramFilter === 'all' ? '' : inviteProgramFilter}
                        onChange={v => setInviteProgramFilter(v || 'all')}
                        options={programs.map(p => ({
                          value: p.id,
                          label: p.name || p.code || p.id,
                        }))}
                        placeholder="Search programs…"
                        clearLabel="All Programs"
                        emptyLabel="No programs found"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500">Term</label>
                      <select
                        value={inviteTermFilter}
                        onChange={e => setInviteTermFilter(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg p-1.5 font-semibold text-slate-700"
                      >
                        <option value="all">All Terms</option>
                        {uniqueTerms.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500">Stage</label>
                      <select
                        value={inviteStageFilter}
                        onChange={e => setInviteStageFilter(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg p-1.5 font-semibold text-slate-700"
                      >
                        <option value="all">All Stages</option>
                        {Array.from({ length: 10 }, (_, i) => i + 1).map(stage => (
                          <option key={stage} value={String(stage)}>Stage {stage}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500">Status</label>
                      <select
                        value={inviteStatusFilter}
                        onChange={e => setInviteStatusFilter(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg p-1.5 font-semibold text-slate-700"
                      >
                        <option value="all">All Statuses</option>
                        <option value="Draft">Draft</option>
                        <option value="Submitted">Submitted</option>
                        <option value="Under Review">Under Review</option>
                        <option value="Approved">Approved</option>
                        <option value="Returned">Returned</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </div>
                  </div>

                  {/* Matching Available Candidates */}
                  <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-left border-collapse min-w-[600px]">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 font-bold text-slate-500 text-[10px] uppercase">
                          <th className="p-2.5">Candidate Name / Email</th>
                          <th className="p-2.5">Application Info</th>
                          <th className="p-2.5">Eligibility Check</th>
                          <th className="p-2.5 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredApplicants.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="p-4 text-center text-slate-500 italic">No matching uninvited candidates found.</td>
                          </tr>
                        ) : (
                          filteredApplicants.slice(0, 30).map(app => {
                            const fullName = getSafeApplicantName(app);

                            // Perform event eligibility checks
                            const cfg = ev.groupEventConfig || {};
                            let isEligible = true;
                            let ineligibilityReason = '';

                            if (cfg.eligibleProgramId && cfg.eligibleProgramId !== 'all' && cfg.eligibleProgramId !== '') {
                              if (app.programId !== cfg.eligibleProgramId) {
                                isEligible = false;
                                ineligibilityReason = 'Program mismatch';
                              }
                            }

                            if (isEligible && cfg.eligibleTerm && cfg.eligibleTerm !== 'all' && cfg.eligibleTerm !== '') {
                              if (app.programTerm !== cfg.eligibleTerm) {
                                isEligible = false;
                                ineligibilityReason = 'Term mismatch';
                              }
                            }

                            if (isEligible && cfg.eligibleStage !== undefined && cfg.eligibleStage !== null && cfg.eligibleStage !== 'all' && cfg.eligibleStage !== '') {
                              if (String(app.masterStage) !== String(cfg.eligibleStage)) {
                                isEligible = false;
                                ineligibilityReason = 'Stage mismatch';
                              }
                            }

                            if (isEligible && cfg.eligibleStatus && cfg.eligibleStatus !== 'all' && cfg.eligibleStatus !== '') {
                              if (app.submissionStatus !== cfg.eligibleStatus) {
                                isEligible = false;
                                ineligibilityReason = 'Status mismatch';
                              }
                            }

                            return (
                              <tr key={app.applicationId} className="hover:bg-slate-50/50">
                                <td className="p-2.5">
                                  <div className="font-bold text-slate-800">{fullName}</div>
                                  <div className="text-[11px] text-slate-500 font-mono">{app.userEmail}</div>
                                </td>
                                <td className="p-2.5 font-medium text-slate-600">
                                  <div>ID: {app.applicationId}</div>
                                  <div className="text-[11px] text-slate-500">{app.programType} · {app.programTerm || 'No term'}</div>
                                </td>
                                <td className="p-2.5 font-semibold">
                                  {isEligible ? (
                                    <span className="text-emerald-600 flex items-center gap-1">
                                      <CheckCircle className="h-3.5 w-3.5" /> Eligible
                                    </span>
                                  ) : (
                                    <span className="text-rose-600 flex items-center gap-1" title={ineligibilityReason}>
                                      <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {ineligibilityReason}
                                    </span>
                                  )}
                                </td>
                                <td className="p-2.5 text-center">
                                  <button
                                    onClick={() => inviteApplicant(app.applicationId)}
                                    disabled={!isEligible || isSubmittingInvite}
                                    className="px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-30 disabled:hover:bg-blue-600 rounded-lg font-bold text-[10px] transition-all cursor-pointer inline-flex items-center gap-1"
                                  >
                                    <Plus className="h-3 w-3" /> Invite
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {dashboardTab === 'notes' && (
            <div className="space-y-4 animate-fade-in text-xs">
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Event Notes & Briefing</h3>
                <p className="text-xs text-slate-500 mt-0.5 font-semibold">Manage participant briefing guidelines and secure internal notes.</p>
              </div>

              <div className="space-y-4">
                {/* External Notes */}
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">External Notes (Visible to Applicants - Markdown)</label>
                  <textarea
                    rows={4}
                    value={groupNotesExternalDraft}
                    onChange={(e) => setGroupNotesExternalDraft(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-medium text-slate-800"
                    placeholder="Provide dress code, preparation files, or guidelines..."
                  />
                </div>

                {/* Internal Notes */}
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 font-bold text-slate-700">Internal Notes (Confidential - Admins Only - Rich Text)</label>
                  <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                    <RichTextEditor
                      value={groupNotesInternalDraft}
                      onChange={(html) => setGroupNotesInternalDraft(html)}
                      placeholder="Confidentially outline interview questions, answers, rating matrix, and notes..."
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => handleSaveGroupDashboardNotes(ev.eventId)}
                    disabled={savingGroupNotes}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50 transition-all flex items-center gap-1.5 cursor-pointer text-xs"
                  >
                    {savingGroupNotes ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    <span>Save Event Notes</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {dashboardTab === 'results' && (() => {
            const bookedSlots = (ev.slots || []).filter((s: any) => s.bookingStatus === 'booked' && s.bookedBy);
            return (
              <div className="space-y-4 animate-fade-in text-xs">
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Evaluation & Interview Results</h3>
                  <p className="text-xs text-slate-500 mt-0.5 font-semibold">Grade candidates who booked/attended slots and send automatic interview status emails.</p>
                </div>

                {bookedSlots.length === 0 ? (
                  <div className="text-center py-10 bg-slate-50 border border-slate-200 rounded-xl text-slate-400 font-medium">
                    No active slot bookings or registered candidates found for evaluation.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
                    <table className="w-full text-left border-collapse text-xs min-w-[650px]">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                          <th className="p-3">Slot Time</th>
                          <th className="p-3">Applicant Name</th>
                          <th className="p-3">Email / App ID</th>
                          <th className="p-3 text-center">Status Result</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                        {bookedSlots.map((slot: any) => {
                          const activeResult = selectedSlotResults[slot.id] !== undefined ? selectedSlotResults[slot.id] : (slot.result || '');
                          return (
                            <tr key={slot.id} className="hover:bg-slate-50/50">
                              <td className="p-3 font-mono font-bold text-slate-600">{slot.startTime} - {slot.endTime}</td>
                              <td className="p-3 font-bold text-slate-900">{sanitizeNamePart(slot.bookedBy?.name) || 'Applicant'}</td>
                              <td className="p-3 text-slate-400">
                                <div>{slot.bookedBy?.email}</div>
                                <div className="text-[10px] font-mono text-slate-500">{slot.bookedBy?.applicationId || 'No ID'}</div>
                                {slot.bookedBy?.applicationId && (
                                  <a href={`/applications/adminview/${slot.bookedBy.applicationId}`}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     className="text-[10px] text-blue-600 hover:underline font-extrabold block mt-0.5"
                                     onClick={(e) => e.stopPropagation()}
                                  >
                                    URL: /applications/adminview/{slot.bookedBy.applicationId}
                                  </a>
                                )}
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                  <select
                                    value={activeResult}
                                    onChange={(e) => setSelectedSlotResults(prev => ({ ...prev, [slot.id]: e.target.value }))}
                                    className="bg-white border border-slate-200 rounded-lg p-1.5 font-semibold text-xs text-slate-800"
                                  >
                                    <option value="">Awaiting Evaluation</option>
                                    <option value="Pass">🟢 Pass</option>
                                    <option value="Rejected">🔴 Rejected</option>
                                    <option value="Follow-up Required">🟡 Follow-up Required</option>
                                    <option value="No Show">⚪ No Show</option>
                                  </select>
                                  <button
                                    onClick={() => handleSaveGroupSlotResult(ev.eventId || ev.id, slot.id, activeResult)}
                                    disabled={!activeResult || submittingResultSlotId === slot.id}
                                    className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-30 disabled:hover:bg-blue-600 text-white font-bold rounded-lg transition-all text-[10px] cursor-pointer"
                                  >
                                    {submittingResultSlotId === slot.id ? 'Saving...' : 'Submit'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    ) : (
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Navigation Tabs for Individual Event */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-5 py-2 text-xs shrink-0 font-bold">
          <button
            onClick={() => setIndividualTab('details')}
            className={`px-4 py-2 rounded-lg transition-all ${individualTab === 'details' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Details
          </button>
          <button
            onClick={() => {
              setIndividualTab('notes');
              setIndividualNotesInternalDraft(ev.internalNotes || '');
              setIndividualNotesExternalDraft(ev.externalNotes || ev.description || '');
            }}
            className={`px-4 py-2 rounded-lg transition-all ${individualTab === 'notes' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Notes
          </button>
          <button
            onClick={() => {
              setIndividualTab('results');
              setIndividualResultValue(ev.result || '');
            }}
            className={`px-4 py-2 rounded-lg transition-all ${individualTab === 'results' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          >
            Results
          </button>
        </div>

        {/* Tab Content Panel */}
        <div className="p-5 flex-1 overflow-y-auto space-y-5 text-xs text-slate-700">
          {individualTab === 'details' && (
            <div className="space-y-5">
              {/* Title & Badges Row */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="space-y-1.5 flex-1 min-w-0">
              <span className="text-caption uppercase font-bold text-slate-400 tracking-widest">Event Title</span>
              <p className="text-xl font-black text-slate-900 font-display leading-tight break-words">{ev.title}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-caption font-extrabold uppercase border-2 ${
              ev.eventType === 'Embassy' ? 'bg-blue-50 border-blue-200 text-brand' :
              ev.eventType === 'Interview' ? 'bg-blue-50 border-blue-200 text-brand' :
              ev.eventType === 'Orientation' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
              ev.eventType === 'Training' ? 'bg-amber-50 border-amber-200 text-amber-700' :
              ev.eventType === 'Flight' ? 'bg-blue-50 border-blue-200 text-brand' :
              ev.eventType === 'Medical' ? 'bg-rose-50 border-rose-200 text-rose-700' :
              'bg-slate-50 border-slate-200 text-slate-700'
              }`}>
              {ev.eventType === 'Embassy' && <Shield className="h-3 w-3" />}
              {ev.eventType === 'Interview' && <MessageSquare className="h-3 w-3" />}
              {ev.eventType === 'Orientation' && <Compass className="h-3 w-3" />}
              {ev.eventType === 'Training' && <Award className="h-3 w-3" />}
              {ev.eventType === 'Flight' && <Briefcase className="h-3 w-3" />}
              {ev.eventType === 'Medical' && <AlertCircle className="h-3 w-3" />}
              {ev.eventType}
              </span>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-caption font-extrabold uppercase border-2 ${
              ev.status === 'Completed' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
              ev.status === 'Cancelled' ? 'bg-rose-50 border-rose-200 text-rose-700' :
              'bg-amber-50 border-amber-200 text-amber-700'
              }`}>
              <div className={`h-1.5 w-1.5 rounded-full ${
              ev.status === 'Completed' ? 'bg-emerald-500' :
              ev.status === 'Cancelled' ? 'bg-rose-500' :
              'bg-amber-500'
              }`} />
              {ev.status}
              </span>
              </div>
              </div>

              {/* Detail Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl p-4 border border-slate-200/60 space-y-2.5 shadow-sm">
              <div className="flex items-center gap-2 text-brand">
              <Calendar className="h-4 w-4" />
              <span className="text-caption uppercase font-bold text-slate-500 tracking-wider">Date & Time</span>
              </div>
              <p className="font-bold text-slate-800 font-mono text-sm leading-relaxed">
              {new Date(ev.dateTime).toLocaleString(undefined, {
              weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
              ...(ev.timezone ? { timeZone: ev.timezone, timeZoneName: 'short' } : {})
              })}
              </p>
              {ev.timezone && (
              <p className="text-caption font-semibold text-slate-400 mt-1">{ev.timezone}</p>
              )}
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-xl p-4 border border-slate-200/60 space-y-2.5 shadow-sm">
              <div className="flex items-center gap-2 text-brand">
              <MapPin className="h-4 w-4" />
              <span className="text-caption uppercase font-bold text-slate-500 tracking-wider">Location</span>
              </div>
              <p className="font-bold text-slate-800 text-sm leading-relaxed break-all">{ev.location || '—'}</p>
              {ev.isVirtual && ev.meetingUrl && ev.status !== 'Cancelled' && (
                <a
                  href={ev.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white text-xs font-bold transition-all cursor-pointer"
                >
                  <Video className="h-4 w-4" />
                  Join Meeting Room
                </a>
              )}
              </div>
              </div>

              {/* Description */}
              {(ev.externalNotes || ev.description) && (
              <div className="bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-xl p-4 border border-slate-200/60 space-y-2.5 shadow-sm">
              <div className="flex items-center gap-2 text-brand">
              <FileText className="h-4 w-4" />
              <span className="text-caption uppercase font-bold text-slate-500 tracking-wider">Description & Instructions</span>
              </div>
              <p className="text-sm text-slate-600 font-medium leading-relaxed whitespace-pre-wrap">{ev.externalNotes || ev.description}</p>
              </div>
              )}

              {/* Associated Applicant */}
              {ev.applicantId && (() => {
              const app = applications.find(a => a.applicationId === ev.applicantId);
              if (!app) return null;
              return (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50/40 rounded-xl p-4 border border-blue-200/60 space-y-3 shadow-sm">
              <div className="flex items-center gap-2 text-brand">
              <Users className="h-4 w-4" />
              <span className="text-caption uppercase font-bold tracking-wider">Linked Applicant</span>
              </div>
              <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 overflow-hidden flex items-center justify-center shrink-0 border-2 border-white shadow-md text-sm font-bold text-white">
              {app.profilePhotoUrl ? (
              <img src={app.profilePhotoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
              getSafeApplicantName(app).charAt(0) || 'U'
              )}
              </div>
              <div className="min-w-0 flex-1">
              <p className="font-bold text-slate-900 text-sm">
              {getSafeApplicantName(app)}
              </p>
              <p className="text-xs text-slate-500 font-medium truncate">{app.userEmail}</p>
              <a href={`/applications/adminview/${app.applicationId}`}
                 target="_blank"
                 rel="noopener noreferrer"
                 className="text-[10px] text-blue-600 hover:underline font-extrabold block mt-0.5"
                 onClick={(e) => e.stopPropagation()}
              >
                URL: /applications/adminview/{app.applicationId}
              </a>
              <p className="text-caption text-slate-400 font-semibold mt-0.5">
              {(() => {
                const matchedProg = (programs || []).find((x: any) => x.id === app.programId || x.programId === app.programId || x.name === app.programType || x.code === app.programType);
                const stages = getPipelineStages(matchedProg);
                const stgLabel = stageLabel(stages, (app as any).masterStage, app.status || '—');
                return `Program: ${app.programType || app.programId || '—'} · Stage: ${stgLabel}`;
              })()}
              </p>
              </div>
              <button
              onClick={() => onViewApplicant(app)}
              className="text-slate-400 hover:text-brand bg-white bg-brand-active border border-slate-200 hover:border-blue-200 rounded-xl p-2 transition-all cursor-pointer shrink-0 shadow-sm"
              title="View applicant in the workspace"
              >
              <Eye className="h-4 w-4" />
              </button>
              </div>
              </div>
              );
              })()}

              {/* Metadata */}
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-caption text-slate-400 font-medium border-t border-slate-100 pt-4">
              <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Created: {new Date(ev.createdAt).toLocaleDateString()}</span>
              <span className="flex items-center gap-1.5 font-mono">ID: {ev.eventId}</span>
              </div>
            </div>
          )}

          {individualTab === 'notes' && (
            <div className="space-y-4 animate-fade-in text-xs">
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Event Notes & Briefing</h3>
                <p className="text-xs text-slate-500 mt-0.5 font-semibold">Manage participant briefing guidelines and secure internal notes.</p>
              </div>

              <div className="space-y-4">
                {/* External Notes */}
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400">External Notes (Visible to Applicant - Markdown)</label>
                  <textarea
                    rows={4}
                    value={individualNotesExternalDraft}
                    onChange={(e) => setIndividualNotesExternalDraft(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-medium text-slate-800"
                    placeholder="Provide dress code, preparation files, or guidelines..."
                  />
                </div>

                {/* Internal Notes */}
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 font-bold text-slate-700">Internal Notes (Confidential - Admins Only)</label>
                  <textarea
                    rows={4}
                    value={individualNotesInternalDraft}
                    onChange={(e) => setIndividualNotesInternalDraft(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:bg-white focus:border-brand font-medium text-slate-800"
                    placeholder="Confidentially outline interview questions, candidate rating, or feedback logs..."
                  />
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => handleSaveIndividualNotes(ev.eventId)}
                    disabled={savingIndividualNotes}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50 transition-all flex items-center gap-1.5 cursor-pointer text-xs"
                  >
                    {savingIndividualNotes ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    <span>Save Event Notes</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {individualTab === 'results' && (
            <div className="space-y-4 animate-fade-in text-xs">
              <div>
                <h3 className="font-bold text-slate-900 text-sm">Evaluation & Interview Result</h3>
                <p className="text-xs text-slate-500 mt-0.5 font-semibold">Set the interview status for this candidate. They will receive an automated email notification.</p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block">Candidate Evaluation Status</label>
                  <select
                    value={individualResultValue}
                    onChange={(e) => setIndividualResultValue(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl p-2.5 font-semibold text-xs text-slate-800 outline-none focus:border-brand"
                  >
                    <option value="">Awaiting Evaluation</option>
                    <option value="Pass">🟢 Pass</option>
                    <option value="Rejected">🔴 Rejected</option>
                    <option value="Follow-up Required">🟡 Follow-up Required</option>
                    <option value="No Show">⚪ No Show</option>
                  </select>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => handleSaveIndividualResult(ev.eventId)}
                    disabled={!individualResultValue || submittingIndividualResult}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl disabled:opacity-50 transition-all flex items-center gap-1.5 cursor-pointer text-xs"
                  >
                    {submittingIndividualResult ? 'Submitting...' : 'Submit Evaluation Result'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Footer — matches Create Event footer */}
    <div className="shrink-0 px-5 py-4 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between">
    <button
    onClick={() => { setFocusedEvent(null); setSlideInEvent(null); }}
    className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
    >
    <X className="h-3.5 w-3.5" />
    Close
    </button>
    <div className="flex items-center gap-2">
    <button
    onClick={() => {
      if (isGroup) {
        handleEditEventClickLocal(ev);
      } else {
        handleEditEventClick(ev);
      }
      setFocusedEvent(null); setSlideInEvent(null);
    }}
    className="px-4 py-2 text-xs font-bold btn-primary  text-white rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
    >
    <Edit3 className="h-3.5 w-3.5" />
    <span>Edit Event</span>
    </button>
    <button
    onClick={() => {
    handleDeleteEvent(ev.eventId, ev.title);
    setFocusedEvent(null); setSlideInEvent(null);
    }}
    className="px-4 py-2 text-xs font-bold border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-xl transition-all cursor-pointer flex items-center gap-1.5"
    >
    <Trash2 className="h-3.5 w-3.5" />
    <span>Delete</span>
    </button>
    </div>
    </div>
    </div>
    </Modal>
    );
    })()}

    {/* EXPORT MODAL */}
    {showExportModal && (
    <Modal onClose={() => setShowExportModal(false)} title="Export Events">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-extrabold text-slate-900">Export Events</h3>
            <p className="text-caption text-slate-400 mt-0.5">{filteredEvents.length} events available</p>
          </div>
          <button onClick={() => setShowExportModal(false)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <p className="text-caption text-slate-400">Select fields to include in the CSV export.</p>
          <div className="space-y-2">
            {EVENT_EXPORT_FIELDS.map(f => (
              <label key={f.id} className="flex items-center space-x-2.5 py-1.5 px-1 hover:bg-slate-50 rounded-lg cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={selectedExportFields.includes(f.id)}
                  onChange={() => {
                    setSelectedExportFields(prev =>
                      prev.includes(f.id) ? prev.filter(x => x !== f.id) : [...prev, f.id]
                    );
                  }}
                  className="rounded border-slate-300 text-brand focus:ring-brand-primary"
                />
                <span className="font-semibold text-slate-700">{f.label}</span>
              </label>
            ))}
          </div>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <button
            onClick={() => setShowExportModal(false)}
            className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleExportEvents}
            className="px-4 py-1.5 btn-primary  text-white text-caption font-bold rounded-xl transition-all cursor-pointer flex items-center space-x-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            <span>Download CSV</span>
          </button>
        </div>
      </div>
    </Modal>
    )}

      {/* SCHEDULING INVITE MODAL */}
      {showSchedulingModal && (
        <Modal onClose={() => setShowSchedulingModal(false)} title="Invite Applicant for Event">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-900">Invite Applicant for Event</h3>
              <p className="text-xs text-slate-500 mt-0.5">Send a scheduling link so the applicant can pick a time</p>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Applicant */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">Applicant *</label>
                <SearchableSelect
                  value={eventApplicantId}
                  onChange={v => setEventApplicantId(v)}
                  options={applications.map(app => ({
                    value: app.applicationId,
                    label: `${getSafeApplicantName(app)} (${app.programType}) - ${app.userEmail}`,
                    hint: app.applicationId ? `Applicant ID: ${app.applicationId}` : undefined,
                  }))}
                  placeholder="Search applicants…"
                  clearLabel="-- Select --"
                  emptyLabel="No applicants found"
                />
              </div>
              {/* Title */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">Event Title *</label>
                <input type="text" value={eventTitle} onChange={e => setEventTitle(e.target.value)}
                  placeholder="e.g. Screening Interview"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white" />
              </div>
              {/* Description */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">Description (optional)</label>
                <textarea rows={2} value={eventDescription} onChange={e => setEventDescription(e.target.value)}
                  placeholder="Brief description for the applicant"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white resize-none" />
              </div>
              {/* Assign to Agent */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">Assign To (Agent) *</label>
                <SearchableSelect
                  value={schedulingAgentId ? `${schedulingAgentId}|${schedulingAgentName}` : ''}
                  onChange={v => {
                    const idx = v.indexOf('|');
                    if (idx > 0) {
                      setSchedulingAgentId(v.slice(0, idx));
                      setSchedulingAgentName(v.slice(idx + 1));
                    }
                  }}
                  options={agentsList.map(agent => ({
                    value: `${agent.uid}|${agent.name}`,
                    label: `${agent.name} (${agent.email})${agent.role ? ` {${agent.role}}` : ''}`,
                  }))}
                  placeholder="Search agents…"
                  clearLabel="-- Select agent --"
                  emptyLabel={agentsError ? 'Failed to load agents — close and reopen to retry' : !agentsLoaded ? 'Loading agents...' : 'No agents found'}
                />
              </div>
              {/* Duration */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600">Duration (minutes)</label>
                <select value={schedulingDuration} onChange={e => setSchedulingDuration(parseInt(e.target.value))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white">
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                </select>
              </div>
              {/* Result */}
              {inviteResult && (
                <div className={`flex items-start gap-2 text-xs font-bold px-4 py-3 rounded-xl ${inviteResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {inviteResult.success ? <CheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> : <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
                  <div>
                    {inviteResult.success ? 'Scheduling invite sent!' : 'Failed to send invite.'}
                    {inviteResult.url && (
                      <a href={inviteResult.url} target="_blank" rel="noopener noreferrer" className="block mt-1 text-brand underline">Open scheduling link</a>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
              <button onClick={() => { setShowSchedulingModal(false); setInviteResult(null); }}
                className="px-3 py-1.5 border border-slate-200 text-xs font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">Cancel</button>
              <button onClick={async () => {
                if (!eventApplicantId || !eventTitle.trim() || !schedulingAgentId) { notifyToast('warning', 'Please fill in applicant, title, and agent.'); return; }
                const app = applications.find(a => a.applicationId === eventApplicantId);
                if (!app) { notifyToast('warning', 'Applicant not found'); return; }
                setSendingInvite(true);
                setInviteResult(null);
                try {
                  const token = await auth.currentUser?.getIdToken();
                  const res = await fetch('/api/scheduling/create', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({
                      applicantId: eventApplicantId,
                      applicantEmail: app.userEmail || '',
                      applicantName: getSafeApplicantName(app),
                      agentId: schedulingAgentId,
                      agentName: schedulingAgentName,
                      title: eventTitle.trim(),
                      description: eventDescription.trim(),
                      eventType: 'Interview',
                      durationMinutes: schedulingDuration,
                    }),
                  });
                  const data = await res.json();
                  if (data.success && data.schedulingUrl) {
                    setInviteResult({ success: true, url: data.schedulingUrl });
                    // Send email notification
                    try {
                      const { emailSchedulingInvite } = await import('../../lib/emailTriggers');
                      await emailSchedulingInvite(app, eventTitle.trim(), data.schedulingUrl, schedulingAgentName, eventDescription.trim());
                    } catch (emailErr) { console.warn('Scheduling invite email failed:', emailErr); }
                  } else {
                    setInviteResult({ success: false });
                  }
                } catch (err: any) {
                  setInviteResult({ success: false });
                }
                setSendingInvite(false);
              }} disabled={sendingInvite}
                className="px-4 py-1.5 btn-primary text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50 flex items-center gap-1.5">
                {sendingInvite ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {sendingInvite ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
}
